let _profUserId = 'default';
try {
  require('@react-native-async-storage/async-storage').default
    .getItem('userToken').then(t => { if (t) _profUserId = t.slice(0, 16); }).catch(() => {});
} catch (e) {}

/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView,
  StatusBar, TextInput, ActivityIndicator, KeyboardAvoidingView, Platform,
  Image, Alert, Modal, Switch, Dimensions, FlatList,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// ─── Lazy deps ────────────────────────────────────────────────────────────────
let ImagePicker;    try { ImagePicker    = require('expo-image-picker');       } catch { ImagePicker    = null; }
let Print;          try { Print          = require('expo-print');              } catch { Print          = null; }
let Sharing;        try { Sharing        = require('expo-sharing');            } catch { Sharing        = null; }
let FileSystem;     try { FileSystem     = require('expo-file-system/legacy'); } catch { FileSystem     = null; }
let RNDocPicker;    try { RNDocPicker    = require('react-native-document-picker'); } catch { RNDocPicker    = null; }
let ExpoDocPicker;  try { ExpoDocPicker  = require('expo-document-picker');          } catch { ExpoDocPicker  = null; }

const { width, height } = Dimensions.get('window');

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ─── Shared storage keys (same as ProfileScreen / Login / Signup / Dashboard) ─
const PROF_KEYS = {
  PROFILE: 'prof_user_profile',
  DASH:    'prof_dashboard_data',
};
const apiFetch = async (endpoint, opts = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('userToken');
    const res = await fetch(`${API_BASE}${endpoint}`, {
      headers: { 'Content-Type':'application/json','x-api-key':API_KEY, ...(token?{Authorization:`Bearer ${token}`}:{}) },
      ...opts,
    });
    if (!res.ok) return { error:true, status:res.status };
    return res.json();
  } catch (e) { return { error:true, message:e.message }; }
};

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = [
  { id:1, label:'Personal',     icon:'person-outline'          },
  { id:2, label:'Summary',      icon:'document-text-outline'   },
  { id:3, label:'Experience',   icon:'briefcase-outline'       },
  { id:4, label:'Education',    icon:'school-outline'          },
  { id:5, label:'Skills',       icon:'construct-outline'       },
  { id:6, label:'Extras',       icon:'star-outline'            },
  { id:7, label:'Template',     icon:'color-palette-outline'   },
  { id:8, label:'Job Match',    icon:'analytics-outline'       },
  { id:9, label:'Export',       icon:'cloud-upload-outline'    },
];

const SKILL_LEVELS = ['Beginner','Intermediate','Advanced','Expert'];
const SKILL_LEVEL_COLORS = { Beginner:'#94a3b8', Intermediate:'#3B82F6', Advanced:'#10B981', Expert:'#F59E0B' };

const LANG_LEVELS = ['Basic','Conversational','Professional','Fluent','Native'];

const TEMPLATES = [
  { id:'modern',       name:'Modern',       accent:'#0e4e96', bg:'#EFF6FF', desc:'Clean, tech-forward'       },
  { id:'professional', name:'Professional', accent:'#19abb5', bg:'#F0FDFA', desc:'Classic corporate style'    },
  { id:'creative',     name:'Creative',     accent:'#F59E0B', bg:'#FFFBEB', desc:'Bold, design-forward'       },
  { id:'executive',    name:'Executive',    accent:'#1e293b', bg:'#F8FAFC', desc:'Senior leadership roles'    },
  { id:'minimal',      name:'Minimal',      accent:'#6B7280', bg:'#F9FAFB', desc:'Clean, whitespace-led'      },
  { id:'technical',    name:'Technical',    accent:'#8B5CF6', bg:'#F5F3FF', desc:'Engineering & Dev roles'    },
];

const ATS_KEYWORDS = {
  software:    ['Python','React','Node.js','Git','Agile','REST API','SQL','CI/CD','Docker','Kubernetes'],
  mechanical:  ['CAD','SolidWorks','FEA','AutoCAD','GD&T','MATLAB','Lean','ISO','FMEA','Design'],
  electrical:  ['PCB','FPGA','Embedded','C++','VHDL','AutoCAD','Electrical','Circuit','Power','PLC'],
  civil:       ['AutoCAD','Revit','BIM','Structural','Foundation','Civil3D','Geotechnical','STAAD','SAP2000'],
  management:  ['Agile','Scrum','KPI','Stakeholder','Budget','P&L','Strategy','Leadership','PMP','Six Sigma'],
  data:        ['Python','SQL','Power BI','Tableau','Machine Learning','Statistics','Excel','R','ETL','Big Data'],
};

const toStr = v => (v === null || v === undefined ? '' : String(v));

// ─── Helpers ──────────────────────────────────────────────────────────────────
const genId = () => Date.now() + Math.random();
const wordCount = str => (str?.trim().split(/\s+/).filter(Boolean).length || 0);
const scoreColor = s => s >= 80 ? '#10B981' : s >= 60 ? '#F59E0B' : s >= 40 ? '#F97316' : '#EF4444';

const escapeHtml = s =>
  toStr(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');

// ─── Main Component ───────────────────────────────────────────────────────────
export default function BuildResumeScreen({ navigation, route }) {
  // Auth
  const [userId, setUserId] = useState('default');
  useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0,16)); }).catch(() => {});
  }, []);

  // Navigation
  const [activeStep,   setActiveStep]   = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generating,   setGenerating]   = useState(''); // which AI action is running
  const [isSaving,     setIsSaving]     = useState(false);

  // Modals
  const [previewModal,  setPreviewModal]  = useState(false);
  const [versionModal,  setVersionModal]  = useState(false);
  const [coverModal,    setCoverModal]    = useState(false);
  const [exportModal,   setExportModal]   = useState(false);
  const [aiTips,        setAiTips]        = useState(false);
  const [aiSugs,        setAiSugs]        = useState([]);
  const [aiBulletSugs,  setAiBulletSugs]  = useState({ expId:null, items:[] });
  const [coverLetter,   setCoverLetter]   = useState('');

  // Photo
  const [photoUri,    setPhotoUri]    = useState(null);
  const [photoB64,    setPhotoB64]    = useState(null);

  // Step 1 – Personal
  const [fullName,   setFullName]   = useState('');
  const [profession, setProfession] = useState('');
  const [email,      setEmail]      = useState('');
  const [phone,      setPhone]      = useState('');
  const [location,   setLocation]   = useState('');
  const [linkedin,   setLinkedin]   = useState('');
  const [github,     setGithub]     = useState('');
  const [portfolio,  setPortfolio]  = useState('');
  const [nationality,setNationality]= useState('');

  // Step 2 – Summary
  const [summary,     setSummary]     = useState('');

  // Step 3 – Experience
  const [experiences, setExperiences] = useState([{
    id: genId(), title:'', company:'', location:'', startDate:'', endDate:'',
    description:'', achievements:'', isCurrent:false,
  }]);

  // Step 4 – Education
  const [education, setEducation] = useState([{
    id: genId(), degree:'', institution:'', location:'', startDate:'', endDate:'',
    description:'', gpa:'', honors:'',
  }]);

  // Step 5 – Skills (with level)
  const [skills,      setSkills]      = useState([]); // { name, level }
  const [skillInput,  setSkillInput]  = useState('');
  const [skillLevel,  setSkillLevel]  = useState('Intermediate');

  // Step 6 – Extra sections
  const [certifications, setCertifications] = useState([]);
  const [projects,       setProjects]       = useState([]);
  const [languages,      setLanguages]      = useState([]);
  const [awards,         setAwards]         = useState([]);
  const [volunteer,      setVolunteer]      = useState([]);

  // Step 7 – Template
  const [selectedTemplate, setSelectedTemplate] = useState('modern');

  // Step 8 – Job match
  const [jobDesc,     setJobDesc]     = useState('');
  const [jdMatch,     setJdMatch]     = useState(null);   // { score, matched, missing }
  const [atsResult,   setAtsResult]   = useState(null);

  // Step 9 – Score / export
  const [resumeScore,  setResumeScore]  = useState(0);
  const [aiFeedback,   setAiFeedback]   = useState([]);

  // Versions
  const [versions,     setVersions]    = useState([]);
  const DRAFT_KEY = `resume_draft_${_profUserId}`;
  const VERS_KEY  = `resume_versions_${_profUserId}`;

  // ─── Load / save draft ────────────────────────────────────────────────────
  useEffect(() => { loadDraft(); loadVersions(); loadFromDb().catch(()=>{}); }, []);

  const draftData = useMemo(() => ({
    fullName, profession, email, phone, location, linkedin, github, portfolio, nationality,
    summary, experiences, education, skills, certifications, projects, languages, awards, volunteer,
    selectedTemplate, photoUri, photoB64,
  }), [fullName, profession, email, phone, location, linkedin, github, portfolio, nationality,
       summary, experiences, education, skills, certifications, projects, languages, awards, volunteer,
       selectedTemplate, photoUri, photoB64]);

  // Debounced draft save — only persists 600ms after the last keystroke
  const draftSaveTimer = useRef(null);
  useEffect(() => {
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      AsyncStorage.setItem(DRAFT_KEY, JSON.stringify(draftData)).catch(() => {});
    }, 600);
    return () => { if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current); };
  }, [draftData]);

  const loadDraft = async () => {
    try {
      // Priority 1: hydrate from navigation params (came from UploadResumeScreen "Edit Resume")
      const incoming = route?.params?.resumeData;
      if (incoming) {
        const pi = incoming.personalInfo || incoming;
        if (pi.name || pi.fullName) setFullName(toStr(pi.name || pi.fullName));
        if (pi.firstName || pi.lastName) {
          const combined = [pi.firstName, pi.lastName].filter(Boolean).join(' ');
          if (combined && !pi.name && !pi.fullName) setFullName(combined);
        }
        if (pi.profession || incoming.profession || pi.title)
          setProfession(toStr(pi.profession || incoming.profession || pi.title));
        if (pi.email)       setEmail(toStr(pi.email));
        if (pi.phone)       setPhone(toStr(pi.phone));
        if (pi.location)    setLocation(toStr(pi.location));
        if (pi.linkedin)    setLinkedin(toStr(pi.linkedin));
        if (pi.github)      setGithub(toStr(pi.github));
        if (pi.portfolio || pi.website) setPortfolio(toStr(pi.portfolio || pi.website));
        if (pi.nationality) setNationality(toStr(pi.nationality));
        const summaryText = incoming.summary || incoming.professionalSummary || pi.summary;
        if (summaryText) setSummary(toStr(summaryText));
        if (Array.isArray(incoming.experience) && incoming.experience.length) setExperiences(incoming.experience);
        if (Array.isArray(incoming.education) && incoming.education.length)   setEducation(incoming.education);
        if (Array.isArray(incoming.skills))         setSkills(incoming.skills);
        if (Array.isArray(incoming.certifications)) setCertifications(incoming.certifications);
        if (Array.isArray(incoming.projects))       setProjects(incoming.projects);
        if (Array.isArray(incoming.languages))      setLanguages(incoming.languages);
        if (Array.isArray(incoming.awards))         setAwards(incoming.awards);
        if (Array.isArray(incoming.volunteer))      setVolunteer(incoming.volunteer);
        // Skip cached draft when we've been given fresh data
        return;
      }

      // Priority 2: read the cached AsyncStorage draft
      const raw = await AsyncStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.fullName)    setFullName(toStr(d.fullName));
      if (d.profession)  setProfession(toStr(d.profession));
      if (d.email)       setEmail(toStr(d.email));
      if (d.phone)       setPhone(toStr(d.phone));
      if (d.location)    setLocation(toStr(d.location));
      if (d.linkedin)    setLinkedin(toStr(d.linkedin));
      if (d.github)      setGithub(toStr(d.github));
      if (d.portfolio)   setPortfolio(toStr(d.portfolio));
      if (d.nationality) setNationality(toStr(d.nationality));
      if (d.summary)     setSummary(toStr(d.summary));
      if (Array.isArray(d.experiences) && d.experiences.length) setExperiences(d.experiences);
      if (Array.isArray(d.education)   && d.education.length)   setEducation(d.education);
      if (Array.isArray(d.skills))         setSkills(d.skills);
      if (Array.isArray(d.certifications)) setCertifications(d.certifications);
      if (Array.isArray(d.projects))       setProjects(d.projects);
      if (Array.isArray(d.languages))      setLanguages(d.languages);
      if (Array.isArray(d.awards))         setAwards(d.awards);
      if (Array.isArray(d.volunteer))      setVolunteer(d.volunteer);
      if (d.selectedTemplate) setSelectedTemplate(d.selectedTemplate);
      if (d.photoUri)  setPhotoUri(d.photoUri);
      if (d.photoB64)  setPhotoB64(d.photoB64);
      // Also restore from dedicated photo storage (survives URI expiry)
      if (!d.photoUri) {
        try {
          const savedUri = await AsyncStorage.getItem('resume_photo_uri');
          const savedB64 = await AsyncStorage.getItem('resume_photo_b64');
          if (savedUri) setPhotoUri(savedUri);
          if (savedB64) setPhotoB64(savedB64);
        } catch {}
      }
    } catch {}
  };

  const loadVersions = async () => {
    try {
      const raw = await AsyncStorage.getItem(VERS_KEY);
      if (raw) setVersions(JSON.parse(raw));
    } catch {}
  };

  const saveVersion = async (name) => {
    const ver = {
      id: Date.now(), name: name || `Version ${versions.length + 1}`,
      savedAt: new Date().toISOString(), data: draftData,
    };
    const updated = [ver, ...versions].slice(0, 10);
    setVersions(updated);
    await AsyncStorage.setItem(VERS_KEY, JSON.stringify(updated));
    Alert.alert('✅ Saved', `"${ver.name}" saved successfully.`);
  };

  const loadVersion = (ver) => {
    const d = ver.data;
    setFullName(toStr(d.fullName)); setProfession(toStr(d.profession));
    setEmail(toStr(d.email));       setPhone(toStr(d.phone));
    setLocation(toStr(d.location)); setLinkedin(toStr(d.linkedin||''));
    setSummary(toStr(d.summary));
    if (Array.isArray(d.experiences)) setExperiences(d.experiences);
    if (Array.isArray(d.education))   setEducation(d.education);
    if (Array.isArray(d.skills))      setSkills(d.skills);
    if (Array.isArray(d.certifications)) setCertifications(d.certifications);
    setSelectedTemplate(d.selectedTemplate || 'modern');
    setVersionModal(false);
    Alert.alert('Loaded', `"${ver.name}" loaded.`);
  };

  // ─── Photo ────────────────────────────────────────────────────────────────
  const pickPhoto = async (src) => {
    if (!ImagePicker) { Alert.alert('Install', 'npx expo install expo-image-picker'); return; }

    // Step 1 — request permission
    let perm;
    try {
      perm = src === 'camera'
        ? await ImagePicker.requestCameraPermissionsAsync()
        : await ImagePicker.requestMediaLibraryPermissionsAsync();
    } catch { Alert.alert('Permission Error', 'Could not request permission.'); return; }

    if (perm?.status === 'denied' && !perm?.canAskAgain) {
      Alert.alert('Permission Blocked', 'Please enable access in Settings → App → Permissions.'); return;
    }
    if (perm?.status !== 'granted') { Alert.alert('Permission Required', 'Please allow access when prompted.'); return; }

    // Step 2 — pick WITHOUT base64 (base64 in picker crashes on Android)
    try {
      const opts = { allowsEditing:true, aspect:[1,1], quality:0.85 };
      if (src !== 'camera') {
        // Handle SDK version differences
        if (ImagePicker.MediaTypeOptions?.Images !== undefined) opts.mediaTypes = ImagePicker.MediaTypeOptions.Images;
        else if (ImagePicker.MediaType?.IMAGE !== undefined) opts.mediaTypes = [ImagePicker.MediaType.IMAGE];
      }
      const result = src === 'camera'
        ? await ImagePicker.launchCameraAsync(opts)
        : await ImagePicker.launchImageLibraryAsync(opts);

      if (result?.canceled || !result?.assets?.[0]?.uri) return;
      const uri = result.assets[0].uri;

      // Show image immediately from local URI
      setPhotoUri(uri);
      await AsyncStorage.setItem('resume_photo_uri', uri);

      // Step 3 — encode to base64 AFTER picking using expo-file-system
      // Keeps picker fast; avoids memory crash from inline base64 on Android
      try {
        const FileSystem = require('expo-file-system/legacy');
        const raw = await FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.Base64 });
        const mime = uri.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
        const b64 = `data:${mime};base64,${raw}`;
        setPhotoB64(b64);
        await AsyncStorage.setItem('resume_photo_b64', b64);
      } catch {
        // expo-file-system not installed — photo still displays, just no b64 for PDF
        setPhotoB64(null);
      }
    } catch (e) {
      Alert.alert('Error', 'Could not load photo. Please try again.');
      console.warn('BuildResume pickPhoto:', e?.message);
    }
  };

  // ─── Import Resume PDF ────────────────────────────────────────────────────
  const [importing, setImporting] = useState(false);
  const importingRef = useRef(false);

  const importResumePdf = async () => {
    if (importingRef.current) return;
    if (!RNDocPicker && !ExpoDocPicker) { Alert.alert('Missing Package', 'Please install a document picker:\nnpx expo install expo-document-picker'); return; }
    if (!FileSystem) { Alert.alert('Missing Package', 'Please install expo-file-system:\nnpx expo install expo-file-system'); return; }

    importingRef.current = true;
    const docTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];

    let file; // { uri, name, mimeType }
    try {
      if (RNDocPicker) {
        // react-native-document-picker — no stuck-lock issue
        const picked = await RNDocPicker.pickSingle({ type: docTypes, copyTo: 'cachesDirectory' });
        file = { uri: picked.fileCopyUri || picked.uri, name: picked.name, mimeType: picked.type };
      } else {
        // Fallback: expo-document-picker
        const result = await ExpoDocPicker.getDocumentAsync({ type: docTypes, copyToCacheDirectory: true });
        if (result.canceled || !result.assets?.[0]?.uri) { importingRef.current = false; return; }
        const a = result.assets[0];
        file = { uri: a.uri, name: a.name, mimeType: a.mimeType };
      }
    } catch (pickerErr) {
      importingRef.current = false;
      // User cancelled in react-native-document-picker
      if (RNDocPicker && RNDocPicker.isCancel(pickerErr)) return;
      if (/picking in progress/i.test(pickerErr?.message)) {
        Alert.alert('Picker Busy', 'The file picker was busy from a previous action. Please tap Import again.');
      } else {
        Alert.alert('Error', 'Could not open the file picker. Please try again.');
      }
      return;
    }

    try {

      // Validate file size (max 10MB)
      const info = await FileSystem.getInfoAsync(file.uri);
      if (!info.exists) { Alert.alert('Error', 'Could not access the selected file.'); return; }
      if (info.size > 10 * 1024 * 1024) { Alert.alert('File Too Large', 'Please select a file smaller than 10MB.'); return; }

      setImporting(true);

      // Read file as base64
      const b64 = await FileSystem.readAsStringAsync(file.uri, { encoding: FileSystem.EncodingType.Base64 });

      // Send to AI endpoint for parsing
      let parsed = null;
      try {
        const r = await apiFetch('/api/ai/parse-resume', {
          method: 'POST',
          body: JSON.stringify({ file: b64, fileName: file.name, mimeType: file.mimeType }),
        });
        if (!r.error && r.data) parsed = r.data;
      } catch {}

      // Fallback: try plain-text extraction for .txt files
      if (!parsed && file.name?.toLowerCase().endsWith('.txt')) {
        try {
          const txt = await FileSystem.readAsStringAsync(file.uri);
          // Basic line-based extraction
          const lines = txt.split('\n').map(l => l.trim()).filter(Boolean);
          parsed = { fullName: lines[0] || '', profession: lines[1] || '' };
          // Look for email / phone
          for (const line of lines) {
            if (!parsed.email && /\S+@\S+\.\S+/.test(line)) parsed.email = line.match(/\S+@\S+\.\S+/)[0];
            if (!parsed.phone && /[\d\s\-+()]{7,}/.test(line)) parsed.phone = line.match(/[\d\s\-+()]{7,}/)[0]?.trim();
          }
          parsed.summary = lines.slice(2, 6).join(' ');
        } catch {}
      }

      if (parsed) {
        // Populate fields from parsed data
        if (parsed.fullName)    setFullName(toStr(parsed.fullName));
        if (parsed.profession)  setProfession(toStr(parsed.profession));
        if (parsed.email)       setEmail(toStr(parsed.email));
        if (parsed.phone)       setPhone(toStr(parsed.phone));
        if (parsed.location)    setLocation(toStr(parsed.location));
        if (parsed.linkedin)    setLinkedin(toStr(parsed.linkedin));
        if (parsed.github)      setGithub(toStr(parsed.github));
        if (parsed.portfolio)   setPortfolio(toStr(parsed.portfolio));
        if (parsed.nationality) setNationality(toStr(parsed.nationality));
        if (parsed.summary)     setSummary(toStr(parsed.summary));

        if (Array.isArray(parsed.experiences) && parsed.experiences.length) {
          setExperiences(parsed.experiences.map(e => ({
            id: genId(), title: toStr(e.title), company: toStr(e.company), location: toStr(e.location),
            startDate: toStr(e.startDate), endDate: toStr(e.endDate), description: toStr(e.description),
            achievements: toStr(e.achievements), isCurrent: !!e.isCurrent,
          })));
        }
        if (Array.isArray(parsed.education) && parsed.education.length) {
          setEducation(parsed.education.map(e => ({
            id: genId(), degree: toStr(e.degree), institution: toStr(e.institution), location: toStr(e.location),
            startDate: toStr(e.startDate), endDate: toStr(e.endDate), description: toStr(e.description),
            gpa: toStr(e.gpa), honors: toStr(e.honors),
          })));
        }
        if (Array.isArray(parsed.skills) && parsed.skills.length) {
          setSkills(parsed.skills.map(s => typeof s === 'string' ? { name: s, level: 'Intermediate' } : { name: toStr(s.name), level: s.level || 'Intermediate' }));
        }
        if (Array.isArray(parsed.certifications)) setCertifications(parsed.certifications.map(c => typeof c === 'string' ? c : toStr(c.name || c)));
        if (Array.isArray(parsed.languages))      setLanguages(parsed.languages.map(l => typeof l === 'string' ? { name: l, level: 'Professional' } : l));

        Alert.alert('Resume Imported', `Data from "${file.name}" has been loaded into the builder. Review each step to verify and refine.`);
      } else {
        Alert.alert('Import Incomplete', 'Could not extract structured data from this file. You can fill in the fields manually.');
      }
    } catch (e) {
      console.warn('importResumePdf:', e?.message);
      Alert.alert('Import Failed', 'There was an error reading the file. Please try again.');
    } finally {
      setImporting(false);
      importingRef.current = false;
    }
  };

  // ─── Score calc ───────────────────────────────────────────────────────────
  const calcScore = useCallback(() => {
    let s = 0;
    if (fullName)  s += 5;  if (profession) s += 5; if (email) s += 4;
    if (phone)     s += 3;  if (location)   s += 3;
    if (linkedin)  s += 2;  if (github)     s += 2; if (portfolio) s += 1;
    if (summary.length >= 80)  s += 15; else if (summary.length > 0) s += 8;
    s += Math.min(25, experiences.filter(e=>e.title&&e.company).length * 8);
    s += Math.min(15, education.filter(e=>e.degree&&e.institution).length * 7);
    s += Math.min(15, skills.length * 1.5);
    s += certifications.length > 0 ? 3 : 0;
    s += projects.length > 0 ? 2 : 0;
    s += languages.length > 0 ? 2 : 0;
    return Math.min(100, Math.round(s));
  }, [fullName, profession, email, phone, location, linkedin, github, portfolio,
      summary, experiences, education, skills, certifications, projects, languages]);

  // Memoized score value — recomputes only when inputs change, not on every keystroke
  const scoreValue = useMemo(() => calcScore(), [calcScore]);


  // ─── ATS test ─────────────────────────────────────────────────────────────
  const runAts = useCallback((customKeywords = null) => {
    const blob = [summary, ...experiences.map(e=>e.description+' '+e.achievements),
      skills.map(s=>s.name||s).join(' '), profession].join(' ').toLowerCase();

    // Determine keywords: custom JD keywords or profession-based
    let keywords = customKeywords;
    if (!keywords) {
      const cat = Object.keys(ATS_KEYWORDS).find(k => profession.toLowerCase().includes(k)) || 'software';
      keywords = ATS_KEYWORDS[cat];
    }

    const matched = keywords.filter(k => blob.includes(k.toLowerCase()));
    const missing  = keywords.filter(k => !blob.includes(k.toLowerCase()));
    const kwScore  = Math.round((matched.length / Math.max(keywords.length, 1)) * 100);
    const fmtScore = 88; // structured fields = ATS safe
    const secScore = (experiences.some(e=>e.title&&e.company) &&
                      education.some(e=>e.degree&&e.institution) &&
                      skills.length > 0 && summary.length > 0) ? 95 : 72;
    const total    = Math.round(kwScore*0.45 + fmtScore*0.25 + secScore*0.30);

    return { score:total, kwScore, fmtScore, secScore, matched, missing,
      recommendations: [
        'Use exact keywords from the job description',
        'Add measurable achievements (%, £, timeframes)',
        'Keep bullet points concise and action-verb led',
        'Avoid tables, text boxes — use plain text structure',
      ],
    };
  }, [summary, experiences, skills, education, profession]);

  // ─── Job description match ────────────────────────────────────────────────
  const matchToJD = () => {
    if (!jobDesc.trim()) { Alert.alert('Required','Please paste a job description first.'); return; }
    // Extract keywords from JD (words ≥ 4 chars, no stop words)
    const stopWords = new Set(['with','that','this','from','have','will','your','their','they','been','more','when','what']);
    const jdWords = [...new Set(jobDesc.toLowerCase().match(/\b[a-z]{4,}\b/g)||[])].filter(w=>!stopWords.has(w));
    const blob = [summary, ...experiences.map(e=>e.description+' '+e.achievements),
      skills.map(s=>s.name||s).join(' '), profession].join(' ').toLowerCase();
    const matched = jdWords.filter(w => blob.includes(w));
    const missing = jdWords.filter(w => !blob.includes(w)).slice(0,15);
    const score   = Math.round((matched.length / Math.max(jdWords.length, 1)) * 100);
    setJdMatch({ score, matched: matched.length, total: jdWords.length, missing });
    const ats = runAts(jdWords.slice(0,20).map(w=>w.charAt(0).toUpperCase()+w.slice(1)));
    setAtsResult(ats);
  };

  // ─── AI actions ───────────────────────────────────────────────────────────
  const aiGenerateSummary = async () => {
    setGenerating('summary');
    try {
      const r = await apiFetch('/api/ai/resume-summary', {
        method: 'POST',
        body: JSON.stringify({ profession, experience: experiences.map(e=>e.title).filter(Boolean), skills: skills.map(s=>s.name||s) }),
      });
      if (!r.error && r.suggestions) { setAiSugs(r.suggestions); setGenerating(''); return; }
    } catch {}
    // Fallback
    const role = profession || 'Engineer';
    const expYears = experiences.filter(e=>e.title).length > 1 ? `${experiences.filter(e=>e.title).length*2}+` : '5+';
    const topSkills = skills.slice(0,3).map(s=>s.name||s).join(', ') || 'engineering tools';
    setAiSugs([
      `Dedicated ${role} with ${expYears} years of experience driving innovation and delivering high-impact solutions. Proficient in ${topSkills}, with a strong track record of improving efficiency and collaborating across cross-functional teams.`,
      `Results-driven ${role} specialising in delivering complex technical projects on time and within budget. Experienced in ${topSkills}, with a passion for continuous improvement and measurable outcomes.`,
      `Motivated ${role} combining deep technical expertise with excellent stakeholder communication. Skilled in ${topSkills}, adept at translating requirements into robust, scalable solutions.`,
    ]);
    setGenerating('');
  };

  const aiEnhanceBullets = async (expId, currentDesc) => {
    setGenerating(`bullets_${expId}`);
    const exp = experiences.find(e=>e.id===expId);
    try {
      const r = await apiFetch('/api/ai/enhance-bullets', {
        method:'POST',
        body: JSON.stringify({ title:exp?.title, description:currentDesc }),
      });
      if (!r.error && r.bullets) { setAiBulletSugs({ expId, items:r.bullets }); setGenerating(''); return; }
    } catch {}
    setAiBulletSugs({ expId, items: [
      `• Led ${exp?.title||'team'} initiatives resulting in measurable improvements to process efficiency`,
      `• Collaborated with cross-functional teams to deliver projects on schedule and within scope`,
      `• Implemented best practices that reduced errors by 15% and improved delivery time`,
      `• Mentored junior team members and supported knowledge-sharing across the department`,
    ]});
    setGenerating('');
  };

  const aiGenerateCoverLetter = async () => {
    setGenerating('cover');
    try {
      const r = await apiFetch('/api/ai/cover-letter', {
        method:'POST',
        body: JSON.stringify({ name:fullName, profession, summary, skills: skills.map(s=>s.name||s), jobDesc }),
      });
      if (!r.error && r.coverLetter) { setCoverLetter(r.coverLetter); setGenerating(''); return; }
    } catch {}
    setCoverLetter(
`Dear Hiring Manager,

I am writing to express my strong interest in the ${profession || 'position'} role. With extensive experience in ${skills.slice(0,3).map(s=>s.name||s).join(', ')}, I am confident in my ability to make an immediate and lasting contribution to your team.

${summary || 'Throughout my career, I have consistently delivered high-quality results across challenging technical and collaborative environments.'}

I am particularly drawn to this opportunity because it aligns perfectly with my background and professional goals. I look forward to the opportunity to discuss how my skills and experience can benefit your organisation.

Yours sincerely,
${fullName || 'Your Name'}`
    );
    setGenerating('');
    setCoverModal(true);
  };

  const aiAnalyse = async () => {
    setGenerating('analyse');
    const score = calcScore();
    setResumeScore(score);
    const ats = runAts();
    setAtsResult(ats);
    try {
      const r = await apiFetch('/api/ai/resume-feedback', {
        method:'POST',
        body: JSON.stringify({ fullName, profession, summary, experiences, education, skills }),
      });
      if (!r.error && r.feedback) { setAiFeedback(r.feedback); setGenerating(''); return; }
    } catch {}
    setAiFeedback([
      { type:'positive',    message:'Well-structured sections with clear progression' },
      ...(summary.length < 80       ? [{ type:'improvement', message:'Expand your professional summary to 80–150 words' }] : []),
      ...(skills.length < 5         ? [{ type:'improvement', message:'Add more skills — aim for 8–12 relevant skills'   }] : []),
      ...(experiences.filter(e=>e.achievements).length < experiences.length
          ? [{ type:'improvement', message:'Add quantifiable achievements to each experience entry' }] : []),
      ...(certifications.length===0  ? [{ type:'keyword',    message:'Consider adding professional certifications'      }] : []),
      { type:'keyword', message:`Include more job-specific keywords like: ${ats.missing.slice(0,4).join(', ')}` },
    ]);
    setGenerating('');
  };

  // ─── Experience helpers ───────────────────────────────────────────────────
  const addExp = () => setExperiences(prev => [...prev, { id:genId(), title:'', company:'', location:'', startDate:'', endDate:'', description:'', achievements:'', isCurrent:false }]);
  const updExp = (id, field, val) => setExperiences(prev => prev.map(e => e.id===id ? {...e,[field]:val} : e));
  const delExp = (id) => {
    if (experiences.length <= 1) { Alert.alert('Cannot remove', 'At least one entry is required.'); return; }
    setExperiences(prev => prev.filter(e => e.id!==id));
  };

  // ─── Education helpers ────────────────────────────────────────────────────
  const addEdu = () => setEducation(prev => [...prev, { id:genId(), degree:'', institution:'', location:'', startDate:'', endDate:'', description:'', gpa:'', honors:'' }]);
  const updEdu = (id, field, val) => setEducation(prev => prev.map(e => e.id===id ? {...e,[field]:val} : e));
  const delEdu = (id) => {
    if (education.length <= 1) { Alert.alert('Cannot remove', 'At least one entry is required.'); return; }
    setEducation(prev => prev.filter(e => e.id!==id));
  };

  // ─── Skills helpers ───────────────────────────────────────────────────────
  const addSkill = () => {
    const name = skillInput.trim();
    if (!name) return;
    if (skills.some(s => (s.name||s).toLowerCase() === name.toLowerCase())) {
      Alert.alert('Duplicate', 'Skill already added.'); setSkillInput(''); return;
    }
    setSkills(prev => [...prev, { name: name.charAt(0).toUpperCase()+name.slice(1), level: skillLevel }]);
    setSkillInput('');
  };
  const updSkillLevel = (idx, level) => setSkills(prev => prev.map((s,i) => i===idx ? {...s, level} : s));
  const delSkill = (idx) => setSkills(prev => prev.filter((_,i) => i!==idx));

  // ─── Extra section helpers ────────────────────────────────────────────────
  const addCert = () => setCertifications(prev => [...prev, { id:genId(), name:'', issuer:'', date:'', expiry:'', credId:'' }]);
  const updCert = (id, f, v) => setCertifications(prev => prev.map(c => c.id===id ? {...c,[f]:v} : c));
  const delCert = (id) => setCertifications(prev => prev.filter(c=>c.id!==id));

  const addProj = () => setProjects(prev => [...prev, { id:genId(), name:'', description:'', url:'', tech:'' }]);
  const updProj = (id, f, v) => setProjects(prev => prev.map(p => p.id===id ? {...p,[f]:v} : p));
  const delProj = (id) => setProjects(prev => prev.filter(p=>p.id!==id));

  const addLang = () => setLanguages(prev => [...prev, { id:genId(), name:'', level:'Conversational' }]);
  const updLang = (id, f, v) => setLanguages(prev => prev.map(l => l.id===id ? {...l,[f]:v} : l));
  const delLang = (id) => setLanguages(prev => prev.filter(l=>l.id!==id));

  const addAward = () => setAwards(prev => [...prev, { id:genId(), title:'', issuer:'', date:'', description:'' }]);
  const updAward = (id, f, v) => setAwards(prev => prev.map(a => a.id===id ? {...a,[f]:v} : a));
  const delAward = (id) => setAwards(prev => prev.filter(a=>a.id!==id));

  const addVol = () => setVolunteer(prev => [...prev, { id:genId(), role:'', org:'', startDate:'', endDate:'', description:'' }]);
  const updVol = (id, f, v) => setVolunteer(prev => prev.map(v2 => v2.id===id ? {...v2,[f]:v} : v2));
  const delVol = (id) => setVolunteer(prev => prev.filter(v2=>v2.id!==id));

  // ─── Validation ───────────────────────────────────────────────────────────
  const canProceed = () => {
    switch (activeStep) {
      case 1: return !!fullName && !!profession && !!email;
      case 2: return summary.length >= 20;
      case 3: return experiences.some(e=>e.title&&e.company);
      case 4: return education.some(e=>e.degree&&e.institution);
      case 5: return skills.length > 0;
      default: return true;
    }
  };

  const goNext = () => {
    if (!canProceed()) { Alert.alert('Required Fields','Please complete the required fields.'); return; }
    if (activeStep < STEPS.length) setActiveStep(s=>s+1);
  };
  const goPrev = () => { if (activeStep > 1) setActiveStep(s=>s-1); };

  // ─── PDF HTML ─────────────────────────────────────────────────────────────
  const tpl = TEMPLATES.find(t=>t.id===selectedTemplate) || TEMPLATES[0];

  // Lazy HTML builder — only called when preview/export opens, never during typing
  const buildResumeHtml = () => {
    const photo = photoB64
      ? `<img src="${photoB64}" style="width:88px;height:88px;border-radius:50%;object-fit:cover;border:3px solid ${tpl.accent};" />`
      : `<div style="width:88px;height:88px;border-radius:50%;background:${tpl.bg};border:2px solid ${tpl.accent};display:flex;align-items:center;justify-content:center;color:${tpl.accent};font-size:28px;font-weight:800;">${fullName?.charAt(0)||'?'}</div>`;

    const section = (title, content) =>
      `<div style="margin-top:18px;"><div style="font-size:13px;font-weight:900;color:${tpl.accent};border-bottom:2px solid ${tpl.accent};padding-bottom:5px;margin-bottom:10px;text-transform:uppercase;letter-spacing:1px;">${title}</div>${content}</div>`;

    const expHtml = experiences.filter(e=>e.title||e.company).map(e=>`
      <div style="margin-bottom:12px;">
        <div style="font-weight:800;color:#0f172a;font-size:13px;">${escapeHtml(e.title)}</div>
        <div style="color:#374151;font-weight:600;font-size:12px;">${escapeHtml(e.company)}${e.location?` · ${escapeHtml(e.location)}`:''}
          ${e.startDate||e.endDate?` <span style="color:#64748b;">| ${escapeHtml(e.startDate)}–${e.isCurrent?'Present':escapeHtml(e.endDate)}</span>`:''}
        </div>
        ${e.description?`<div style="color:#374151;margin-top:5px;font-size:12px;line-height:1.6;">${escapeHtml(e.description)}</div>`:''}
        ${e.achievements?`<div style="color:#0f172a;margin-top:4px;font-size:12px;font-weight:600;">🏆 ${escapeHtml(e.achievements)}</div>`:''}
      </div>`).join('');

    const eduHtml = education.filter(e=>e.degree||e.institution).map(e=>`
      <div style="margin-bottom:10px;">
        <div style="font-weight:800;color:#0f172a;font-size:13px;">${escapeHtml(e.degree)}</div>
        <div style="color:#374151;font-size:12px;">${escapeHtml(e.institution)}${e.location?` · ${escapeHtml(e.location)}`:''}
          ${e.startDate||e.endDate?` | ${escapeHtml(e.startDate)}–${escapeHtml(e.endDate)}`:''}
          ${e.gpa?` · GPA: ${escapeHtml(e.gpa)}`:''}
        </div>
        ${e.honors?`<div style="color:#374151;font-size:12px;margin-top:3px;">${escapeHtml(e.honors)}</div>`:''}
      </div>`).join('');

    const skillsHtml = skills.map(s=>{
      const name = s.name||s; const lvl = s.level||'';
      const lc = SKILL_LEVEL_COLORS[lvl]||tpl.accent;
      return `<span style="display:inline-block;background:${tpl.bg};border:1.5px solid ${lc};color:${lc};padding:4px 10px;border-radius:20px;margin:3px;font-size:11px;font-weight:700;">${escapeHtml(name)}${lvl?` <span style="opacity:0.7">(${lvl})</span>`:''}</span>`;
    }).join('');

    const certHtml = certifications.filter(c=>c.name).map(c=>`
      <div style="margin-bottom:8px;font-size:12px;">
        <span style="font-weight:700;">${escapeHtml(c.name)}</span> — ${escapeHtml(c.issuer)}
        ${c.date?` <span style="color:#64748b;">| ${escapeHtml(c.date)}</span>`:''}
        ${c.credId?` <span style="color:#64748b;">ID: ${escapeHtml(c.credId)}</span>`:''}
      </div>`).join('');

    const projHtml = projects.filter(p=>p.name).map(p=>`
      <div style="margin-bottom:10px;">
        <div style="font-weight:800;font-size:13px;">${escapeHtml(p.name)}${p.url?` <a href="${escapeHtml(p.url)}" style="color:${tpl.accent};font-size:11px;">[link]</a>`:''}</div>
        ${p.tech?`<div style="font-size:11px;color:#64748b;margin:2px 0;">${escapeHtml(p.tech)}</div>`:''}
        ${p.description?`<div style="font-size:12px;color:#374151;">${escapeHtml(p.description)}</div>`:''}
      </div>`).join('');

    const langHtml = languages.filter(l=>l.name).map(l=>
      `<span style="background:#f1f5f9;padding:4px 10px;border-radius:14px;margin:3px;font-size:12px;font-weight:600;display:inline-block;">${escapeHtml(l.name)} <span style="color:#64748b;">(${escapeHtml(l.level)})</span></span>`
    ).join('');

    const awardHtml = awards.filter(a=>a.title).map(a=>`
      <div style="margin-bottom:8px;font-size:12px;">
        <span style="font-weight:700;">🏅 ${escapeHtml(a.title)}</span> — ${escapeHtml(a.issuer)} ${a.date?`(${escapeHtml(a.date)})`:''}
        ${a.description?`<div style="color:#374151;margin-top:2px;">${escapeHtml(a.description)}</div>`:''}
      </div>`).join('');

    const contacts = [location, email, phone, linkedin&&`linkedin.com/in/${linkedin}`, github&&`github.com/${github}`, portfolio].filter(Boolean);

    return `<html><head><meta charset="utf-8"/><style>
      *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box;}
      body{margin:0;padding:24px;color:#0f172a;background:#fff;font-size:13px;}
      a{color:${tpl.accent};}
    </style></head><body>
      <div style="display:flex;align-items:flex-start;gap:18px;padding-bottom:18px;border-bottom:3px solid ${tpl.accent};">
        ${photo}
        <div style="flex:1;">
          <div style="font-size:26px;font-weight:900;color:#0f172a;">${escapeHtml(fullName||'Your Name')}</div>
          <div style="font-size:15px;font-weight:700;color:${tpl.accent};margin-top:3px;">${escapeHtml(profession||'Professional Title')}</div>
          <div style="font-size:11px;color:#64748b;margin-top:6px;line-height:1.8;">${contacts.map(c=>`<span style="margin-right:12px;">📍 ${escapeHtml(c)}</span>`).join('')}</div>
        </div>
      </div>
      ${summary?section('Professional Summary',`<div style="line-height:1.7;color:#334155;">${escapeHtml(summary)}</div>`):''}
      ${skills.length?section('Skills',`<div>${skillsHtml}</div>`):''}
      ${expHtml?section('Experience',expHtml):''}
      ${eduHtml?section('Education',eduHtml):''}
      ${certHtml?section('Certifications',certHtml):''}
      ${projHtml?section('Projects',projHtml):''}
      ${langHtml?section('Languages',`<div>${langHtml}</div>`):''}
      ${awardHtml?section('Awards & Recognition',awardHtml):''}
    </body></html>`;
  };  // end buildResumeHtml

  // ─── PDF export ───────────────────────────────────────────────────────────
  const generatePdf = async () => {
    if (!Print || !Sharing) { Alert.alert('Install','npx expo install expo-print expo-sharing'); return; }
    setIsGenerating(true);
    try {
      const file = await Print.printToFileAsync({ html: buildResumeHtml(), base64:false });
      if (!file?.uri) throw new Error('No URI');
      const ok = await Sharing.isAvailableAsync();
      if (ok) await Sharing.shareAsync(file.uri, { mimeType:'application/pdf', dialogTitle:'Share Resume', UTI:'com.adobe.pdf' });
      else Alert.alert('PDF Created', `Saved to: ${file.uri}`);
    } catch { Alert.alert('Error','Could not generate PDF.'); }
    finally { setIsGenerating(false); }
  };

  // ─── Save PDF directly to My Documents folder ────────────────────────────
  const saveResumeToDocuments = async () => {
    if (!Print) { Alert.alert('Install required', 'npx expo install expo-print expo-file-system'); return; }
    setIsSaving(true);
    try {
      // 1. Generate PDF
      const file = await Print.printToFileAsync({ html: buildResumeHtml(), base64: false });
      if (!file?.uri) throw new Error('PDF generation failed');

      // 2. Build document metadata
      const docId       = `doc_${Date.now()}_resume`;
      const pdfName     = `Resume_${(fullName||'My_Resume').replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}`;
      const DOCS_DIR    = FileSystem?.documentDirectory ? `${FileSystem.documentDirectory}my_documents/` : null;
      let   finalPath   = file.uri;

      // 3. Copy to persistent my_documents directory
      if (FileSystem && DOCS_DIR) {
        const info = await FileSystem.getInfoAsync(DOCS_DIR);
        if (!info.exists) await FileSystem.makeDirectoryAsync(DOCS_DIR, { intermediates: true });
        const destPath = `${DOCS_DIR}${docId}.pdf`;
        await FileSystem.copyAsync({ from: file.uri, to: destPath });
        finalPath = destPath;
      }

      // 4. Get file size
      let size = 0;
      if (FileSystem) {
        try { const info = await FileSystem.getInfoAsync(finalPath); size = info.size || 0; } catch {}
      }

      // 5. Save metadata to AsyncStorage (same key MyDocumentsScreen reads)
      const newDoc = {
        id:        docId,
        name:      pdfName,
        fileName:  `${pdfName}.pdf`,
        filePath:  finalPath,
        mimeType:  'application/pdf',
        size,
        category:  'resume',
        dateAdded: new Date().toISOString(),
        source:    'resume',
        resumeMeta: {
          fullName,
          profession,
          template: selectedTemplate,
          score:    calcScore(),
          email,
          phone,
        },
      };

      const raw  = await AsyncStorage.getItem('my_documents_metadata');
      const list = raw ? JSON.parse(raw) : [];
      list.unshift(newDoc);
      await AsyncStorage.setItem('my_documents_metadata', JSON.stringify(list));

      // 6. Also save to applications list so SubmitForJobs can use it
      await saveToApplications(false);

      // 6b. Upload PDF to the VPS so recruiters see the real resume in applicant views
      try {
        // Service handles the user-id lookup, auth header, and endpoint
        const file = {
          uri:  finalPath,
          type: 'application/pdf',
          name: `${pdfName}.pdf`,
        };
        const uploadResult = await ProfileManagementService.uploadResume(file, { documentName: pdfName });
        if (uploadResult.success && (uploadResult.data?.file_url)) {
          console.log('✅ Resume uploaded to VPS:', uploadResult.data.file_url);
          await AsyncStorage.setItem('user_resume_url', uploadResult.data.file_url);
          await AsyncStorage.setItem('user_resume_uploaded_at', new Date().toISOString());
        } else {
          console.warn('Resume upload failed:', uploadResult.error || 'unknown');
        }
      } catch (upErr) {
        console.warn('Resume upload error:', upErr?.message);
      }

      // 7. Ask user what to do next
      Alert.alert(
        '✅ Resume Saved',
        `"${pdfName}" has been saved to My Documents and is ready for job applications.`,
        [
          {
            text: 'Open in Documents',
            onPress: () => navigation.navigate('MyDocuments'),
          },
          {
            text: 'Share PDF',
            onPress: async () => {
              if (Sharing) {
                const ok = await Sharing.isAvailableAsync();
                if (ok) await Sharing.shareAsync(finalPath, { mimeType:'application/pdf', dialogTitle:'Share Resume', UTI:'com.adobe.pdf' });
              }
            },
          },
          { text: 'Done', style: 'cancel' },
        ]
      );
    } catch (e) {
      console.warn('saveResumeToDocuments:', e?.message);
      Alert.alert('Error', 'Could not save resume as PDF. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Save built resume to user_resumes list (used by SubmitForJobsScreen) ──
  const saveToApplications = async (showAlert = true) => {
    try {
      const uid = await AsyncStorage.getItem('userId') || await AsyncStorage.getItem('user_id') || 'guest';
      const key = `user_resumes_${uid}`;
      const raw = await AsyncStorage.getItem(key);
      const list = raw ? JSON.parse(raw) : [];

      const resumeId = `built_resume_${selectedTemplate}_${Date.now()}`;
      const resumeName = `${fullName || 'My Resume'} - ${TEMPLATES.find(t => t.id === selectedTemplate)?.name || 'Resume'}`;

      // Build the resume entry with full data for SubmitForJobs
      const entry = {
        id: resumeId,
        name: resumeName,
        type: 'built_resume',
        source: 'build_resume',
        size: 0,
        uri: null,
        lastUpdated: new Date().toISOString(),
        matchScore: calcScore(),
        isDefault: list.length === 0,
        template: selectedTemplate,
        // Embed full resume data so SubmitForJobs can display & submit it
        resumeData: {
          fullName, profession, email, phone, location, linkedin, github, portfolio, nationality,
          summary, experiences, education, skills: skills.map(s => ({ name: s.name || s, level: s.level || 'Intermediate' })),
          certifications, projects, languages, awards, volunteer,
          selectedTemplate, photoUri, photoB64,
          atsScore: atsResult?.score || null,
          resumeScore: calcScore(),
        },
      };

      // Replace existing built resume of same template, or add new
      const idx = list.findIndex(r => r.source === 'build_resume' && r.template === selectedTemplate);
      if (idx >= 0) list[idx] = entry;
      else list.unshift(entry);

      await AsyncStorage.setItem(key, JSON.stringify(list));

      // If navigated from SubmitForJobs with returnTo param, go back
      const returnTo = route?.params?.returnTo;
      if (returnTo) {
        navigation.navigate(returnTo, { selectedBuiltResume: entry });
      } else if (showAlert) {
        Alert.alert('✅ Ready for Applications', `"${resumeName}" is now available when you apply for jobs.`);
      }
    } catch (e) {
      console.warn('saveToApplications:', e?.message);
      if (showAlert) Alert.alert('Error', 'Could not save resume for applications.');
    }
  };

  // ── Resolve user ID from all storage keys ─────────────────────────────────
  const resolveUid = async () => {
    for (const k of ['userId','user_id','professional_user_id']) {
      const v = await AsyncStorage.getItem(k);
      if (v && !isNaN(parseInt(v))) return parseInt(v);
    }
    return null;
  };

  // ── Skill level string → DB integer ──────────────────────────────────────
  const SKILL_LVL = {Beginner:1,Intermediate:2,Advanced:3,Expert:4};

  // ── Save all resume sections to their real DB tables ─────────────────────
  const saveToDb = async (uid) => {
    if (!uid) return;

    // 1. Personal fields → users table + business_users table
    const usersPayload = {
      full_name:   fullName||'', email:email||'', phone:phone||'',
      bio:         summary||'', about:summary||'',
      location:    location||'', job_title:profession||'', position:profession||'',
      linkedin:    linkedin||'', github:github||'',
      personal_website:portfolio||'', portfolio_url:portfolio||'',
      nationality: nationality||'',
      profile_image: photoB64||photoUri||'',
    };
    await apiFetch(`/api/users/${uid}`,{method:'PUT',body:JSON.stringify(usersPayload)}).catch(()=>{});
    await apiFetch(`/api/business-users/${uid}`,{method:'PUT',body:JSON.stringify({...usersPayload,linkedin_url:linkedin||''})}).catch(()=>{});

    // 2. Work experience → user_work_experience
    for (const exp of experiences) {
      const payload = {
        company_name: exp.company||'', job_title: exp.title||exp.role||'',
        start_date:   exp.startDate||null,
        end_date:     exp.isCurrent ? null : (exp.endDate||null),
        is_current:   !!exp.isCurrent,
        description:  (exp.description||'')+(exp.achievements?'\n'+exp.achievements:''),
        location:     exp.location||'',
      };
      if (exp._dbId) {
        await apiFetch(`/api/users/${uid}/experience/${exp._dbId}`,{method:'PUT',body:JSON.stringify(payload)}).catch(()=>{});
      } else {
        const r = await apiFetch(`/api/users/${uid}/experience`,{method:'POST',body:JSON.stringify(payload)}).catch(()=>null);
        if (r?.data?.id) exp._dbId = r.data.id;
      }
    }

    // 3. Education → user_education
    for (const edu of education) {
      const payload = {
        institution:  edu.institution||'', degree:edu.degree||'',
        field_of_study: edu.fieldOfStudy||'',
        start_date:   edu.startDate||null, end_date:edu.endDate||null,
        grade:        edu.gpa||edu.grade||'', description:edu.description||'',
      };
      if (edu._dbId) {
        await apiFetch(`/api/users/${uid}/education/${edu._dbId}`,{method:'PUT',body:JSON.stringify(payload)}).catch(()=>{});
      } else {
        const r = await apiFetch(`/api/users/${uid}/education`,{method:'POST',body:JSON.stringify(payload)}).catch(()=>null);
        if (r?.data?.id) edu._dbId = r.data.id;
      }
    }

    // 4. Skills → user_skills
    for (const sk of skills) {
      const name = sk.name||(typeof sk==='string'?sk:'');
      if (!name) continue;
      const payload = { skill_name:name, skill_level: SKILL_LVL[sk.level]||2 };
      if (sk._dbId) {
        await apiFetch(`/api/users/${uid}/skills/${sk._dbId}`,{method:'PUT',body:JSON.stringify(payload)}).catch(()=>{});
      } else {
        const r = await apiFetch(`/api/users/${uid}/skills`,{method:'POST',body:JSON.stringify(payload)}).catch(()=>null);
        if (r?.data?.id) sk._dbId = r.data.id;
      }
    }

    // 5. Certifications → user_certifications
    for (const cert of certifications) {
      const payload = {
        cert_name:            cert.name||'',
        issuing_organization: cert.issuer||cert.issuingOrganization||'',
        issue_date:           cert.date||cert.issueDate||null,
        expiry_date:          cert.expiry||cert.expiryDate||null,
        credential_id:        cert.credentialId||'',
        credential_url:       cert.url||cert.credentialUrl||'',
      };
      if (cert._dbId) {
        await apiFetch(`/api/users/${uid}/certifications/${cert._dbId}`,{method:'PUT',body:JSON.stringify(payload)}).catch(()=>{});
      } else {
        const r = await apiFetch(`/api/users/${uid}/certifications`,{method:'POST',body:JSON.stringify(payload)}).catch(()=>null);
        if (r?.data?.id) cert._dbId = r.data.id;
      }
    }

    // 6. Languages → user_languages
    for (const lang of languages) {
      if (!lang.name) continue;
      if (!lang._dbId) {
        const payload = { language_name:lang.name, proficiency_level:(lang.level||'conversational').toLowerCase() };
        const r = await apiFetch(`/api/users/${uid}/languages`,{method:'POST',body:JSON.stringify(payload)}).catch(()=>null);
        if (r?.data?.id) lang._dbId = r.data.id;
      }
    }
  };

  // ── Load profile data FROM DB into BuildResume state ─────────────────────
  const loadFromDb = async () => {
    try {
      const uid = await resolveUid(); if (!uid) return;
      const r = await apiFetch(`/api/users/${uid}/profile`);
      if (!r.error && r.data) {
        const d = r.data;
        // Personal
        if (d.full_name)   setFullName(d.full_name);
        if (d.email)       setEmail(d.email);
        if (d.phone)       setPhone(d.phone);
        if (d.location)    setLocation(d.location);
        if (d.job_title||d.position) setProfession(d.job_title||d.position||'');
        if (d.bio||d.about)  setSummary(d.bio||d.about||'');
        if (d.linkedin)    setLinkedin(d.linkedin);
        if (d.github)      setGithub(d.github);
        if (d.personal_website||d.portfolio_url) setPortfolio(d.personal_website||d.portfolio_url||'');
        if (d.nationality) setNationality(d.nationality);
        if (d.profile_image) { setPhotoUri(d.profile_image); setPhotoB64(d.profile_image); }
        // Sub-tables
        if (d.work_experience?.length>0) {
          setExperiences(d.work_experience.map(e=>({
            id:String(e.id), _dbId:e.id,
            title:e.job_title||'', company:e.company_name||'', location:e.location||'',
            startDate:e.start_date?String(e.start_date).split('T')[0]:'',
            endDate:e.end_date?String(e.end_date).split('T')[0]:'',
            isCurrent:!!e.is_current, description:e.description||'', achievements:'',
          })));
        }
        if (d.education?.length>0) {
          setEducation(d.education.map(e=>({
            id:String(e.id), _dbId:e.id,
            degree:e.degree||'', institution:e.institution||'',
            location:'', startDate:e.start_date?String(e.start_date):'',
            endDate:e.end_date?String(e.end_date):'',
            gpa:e.grade||'', honors:'', description:e.description||'',
          })));
        }
        if (d.skills?.length>0) {
          setSkills(d.skills.map(s=>({
            id:String(s.id), _dbId:s.id,
            name:s.skill_name||'',
            level:['Beginner','Intermediate','Advanced','Expert'][Math.min(Math.max((s.skill_level||2)-1,0),3)]||'Intermediate',
          })));
        }
        if (d.certifications?.length>0) {
          setCertifications(d.certifications.map(c=>({
            id:String(c.id), _dbId:c.id,
            name:c.cert_name||'', issuer:c.issuing_organization||'',
            date:c.issue_date?String(c.issue_date).split('T')[0]:'',
            expiry:c.expiry_date?String(c.expiry_date).split('T')[0]:'',
            credentialId:c.credential_id||'', url:c.credential_url||'',
          })));
        }
        if (d.languages?.length>0) {
          setLanguages(d.languages.map(l=>({
            id:String(l.id), _dbId:l.id,
            name:l.language_name||'',
            level:(l.proficiency_level||'conversational').charAt(0).toUpperCase()+(l.proficiency_level||'conversational').slice(1),
          })));
        }
      }
    } catch(e) { console.warn('loadFromDb:',e?.message); }
  };

  const saveToApi = async () => {
    setIsSaving(true);
    try {
      await syncToProfile(false); // sync AsyncStorage + dashboard keys
      await saveToApplications(false); // make available in SubmitForJobs

      // Build the resume data shape that ProfileManagementService expects
      // (and that UploadResumeScreen / ResumePreviewScreen can consume)
      const resumeData = {
        personalInfo: {
          name: fullName,
          fullName,
          email,
          phone,
          location,
          linkedin,
          github,
          portfolio,
          nationality,
          profileImage: photoUri,
        },
        profession,
        summary,
        experience: experiences,
        education,
        skills,
        certifications,
        projects,
        languages,
        awards,
        volunteer,
      };

      // Backend save via service (handles auth + cache + endpoint)
      const serviceResult = await ProfileManagementService.saveProfile(resumeData);

      const uid = await resolveUid();
      if (uid) {
        await saveToDb(uid); // legacy per-table sync still runs for backward compat
        Alert.alert(
          '✅ Saved',
          serviceResult.success
            ? 'Resume data saved to your profile, database, and ready for applications.'
            : 'Resume saved locally. Cloud sync will retry next time.'
        );
      } else {
        Alert.alert('Saved locally', 'Resume saved to device. Sign in to sync to the cloud.');
      }
    } catch(e) {
      Alert.alert('Saved locally', 'Saved to device. Cloud sync failed.');
      console.warn('saveToApi:',e?.message);
    } finally { setIsSaving(false); }
  };

  // ─── Sync BuildResume → ProfileScreen shared storage ────────────────────────
  const syncToProfile = async (showAlert = true) => {
    try {
      // Read existing profile (don't wipe fields not in BuildResume)
      const candidates = await Promise.all([
        AsyncStorage.getItem(PROF_KEYS.PROFILE),
        AsyncStorage.getItem('userProfile'),
      ]);
      const existing = (() => { try { return JSON.parse(candidates.find(Boolean)); } catch { return {}; } })() || {};

      // Map BuildResume fields → ProfileScreen shape
      // Key differences: profession→jobTitle, summary→about, isCurrent→current, portfolio→website
      const merged = {
        ...existing,
        personal: {
          ...(existing.personal || {}),
          fullName:        fullName     || existing.personal?.fullName     || '',
          email:           email        || existing.personal?.email        || '',
          phone:           phone        || existing.personal?.phone        || '',
          location:        location     || existing.personal?.location     || '',
          jobTitle:        profession   || existing.personal?.jobTitle     || '',
          about:           summary      || existing.personal?.about        || '',
          linkedin:        linkedin     || existing.personal?.linkedin     || '',
          github:          github       || existing.personal?.github       || '',
          website:         portfolio    || existing.personal?.website      || '',
          nationality:     nationality  || existing.personal?.nationality  || '',
          profileImage:    photoUri     || existing.personal?.profileImage || null,
          profileImageB64: photoB64     || existing.personal?.profileImageB64 || null,
          availability:    existing.personal?.availability || 'Available',
          noticePeriod:    existing.personal?.noticePeriod || '1 month',
        },
        experience: experiences.length > 0
          ? experiences.map(e => ({
              id:e.id, title:e.title||'', company:e.company||'',
              location:e.location||'', startDate:e.startDate||'',
              endDate:e.endDate||'', current:!!e.isCurrent,
              description:e.description||'', achievements:e.achievements||'',
            }))
          : (existing.experience || []),
        education: education.length > 0
          ? education.map(e => ({
              id:e.id, degree:e.degree||'', institution:e.institution||'',
              location:e.location||'', startDate:e.startDate||'',
              endDate:e.endDate||'', gpa:e.gpa||'', honors:e.honors||'',
              description:e.description||'',
            }))
          : (existing.education || []),
        skills: skills.length > 0
          ? skills.map(s => ({
              id:    s.id    || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
              name:  s.name  || (typeof s==='string'?s:''),
              level: s.level || 'Intermediate',
            }))
          : (existing.skills || []),
        certifications: certifications.length > 0 ? certifications : (existing.certifications||[]),
        projects:       projects.length       > 0 ? projects       : (existing.projects||[]),
        languages:      languages.length      > 0 ? languages      : (existing.languages||[]),
        awards:         awards.length         > 0 ? awards         : (existing.awards||[]),
        volunteer:      existing.volunteer || [],
        preferences:    existing.preferences || {jobType:[],salaryRange:'',workLocation:'',industries:[]},
        privacy:        existing.privacy     || {profileVisibility:'Public',showEmail:true,showPhone:false,showSalary:false,showLocation:true,allowRecruiterContact:true},
      };

      // Compute quick completion %
      const p = merged.personal;
      const filledFields = ['fullName','email','phone','location','jobTitle','about']
        .filter(k=>(p[k]||'').trim()).length;
      const completion = Math.min(100,
        Math.round((filledFields/6)*100)
        + (merged.experience.length>0?8:0)
        + (merged.education.length>0?5:0)
        + (merged.skills.length>=5?10:merged.skills.length*2)
        + (merged.certifications.length>0?4:0)
        + (p.profileImage?5:0)
        + ((p.linkedin||'').trim()?5:0)
      );
      const stats = {completionPercentage:completion,profileStrength:completion,lastUpdated:new Date().toISOString()};

      // Write to all storage keys that ProfileScreen + Dashboard + Home read
      const pj = JSON.stringify(merged);
      const sj = JSON.stringify(stats);
      await Promise.all([
        AsyncStorage.setItem(PROF_KEYS.PROFILE,                   pj),
        AsyncStorage.setItem('userProfile',                        pj),
        AsyncStorage.setItem('professional_default_userProfile',   pj),
        AsyncStorage.setItem('profileStats',                       sj),
      ]);

      // Dashboard sync payload (Home + Dashboard useFocusEffect reads this)
      const dash = {
        user:{
          fullName:p.fullName||'',email:p.email||'',jobTitle:p.jobTitle||'',
          profileImage:p.profileImageB64||p.profileImage||null,
          availability:p.availability||'Available',
        },
        stats:{completionPercentage:completion,completion_percentage:completion,profileStrength:completion},
        skillsCount:merged.skills.length,experienceCount:merged.experience.length,
        educationCount:merged.education.length,certificationsCount:merged.certifications.length,
        lastUpdated:new Date().toISOString(),
      };
      const dj = JSON.stringify(dash);
      await AsyncStorage.setItem(PROF_KEYS.DASH,  dj);
      await AsyncStorage.setItem('dashboardData', dj);

      // Persist photo so ProfileScreen restores it on next open
      if (photoUri) {
        await AsyncStorage.setItem('profile_photo_uri', photoUri);
        await AsyncStorage.setItem('resume_photo_uri',  photoUri);
      }
      if (photoB64) {
        await AsyncStorage.setItem('profile_photo_b64', photoB64);
        await AsyncStorage.setItem('resume_photo_b64',  photoB64);
      }

      if (showAlert) Alert.alert('✅ Profile Updated',
        `All resume data synced to your profile:
• ${merged.experience.length} experience entries
• ${merged.skills.length} skills
• ${merged.education.length} education entries
• ${merged.certifications.length} certifications`);
    } catch(e) {
      console.warn('syncToProfile:', e?.message);
      if (showAlert) Alert.alert('Sync Error', e?.message || 'Could not sync to profile.');
    }
  };

  // Field component moved to module scope (below StyleSheet) for performance.

  // ─── STEP RENDERERS ───────────────────────────────────────────────────────

  const Step1 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Personal Information</Text>
      <Text style={st.stepDesc}>Let's start with your basic details</Text>

      {/* Import Resume PDF */}
      <TouchableOpacity
        style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', backgroundColor:'#EFF6FF', borderWidth:1, borderColor:'#BFDBFE', borderRadius:12, borderStyle:'dashed', paddingVertical:14, paddingHorizontal:16, marginBottom:16 }}
        onPress={importResumePdf}
        disabled={importing}
      >
        {importing
          ? <ActivityIndicator size={18} color="#0e4e96" style={{ marginRight:8 }} />
          : <Ionicons name="document-attach-outline" size={20} color="#0e4e96" style={{ marginRight:8 }} />
        }
        <View>
          <Text style={{ fontSize:14, fontWeight:'700', color:'#0e4e96' }}>
            {importing ? 'Importing...' : 'Import Resume PDF'}
          </Text>
          <Text style={{ fontSize:11, color:'#64748B', marginTop:2 }}>
            Auto-fill from an existing PDF, Word, or text file
          </Text>
        </View>
      </TouchableOpacity>

      {/* Photo */}
      <View style={st.photoCard}>
        <View style={{ flexDirection:'row', alignItems:'center' }}>
          {photoUri
            ? <Image source={{ uri: photoUri }} style={st.photo} />
            : <View style={st.photoPlaceholder}><Ionicons name="person" size={28} color="#94a3b8" /></View>
          }
          <View style={{ marginLeft:14, flex:1 }}>
            <Text style={st.photoTitle}>Profile Photo</Text>
            <Text style={st.photoSub}>Included in PDF export</Text>
          </View>
        </View>
        <View style={{ flexDirection:'row', gap:8, marginTop:12 }}>
          {[
            { label:'Camera',  icon:'camera-outline',  action:()=>pickPhoto('camera')  },
            { label:'Gallery', icon:'images-outline',  action:()=>pickPhoto('gallery') },
            { label:'Remove',  icon:'trash-outline',   action:()=>{setPhotoUri(null);setPhotoB64(null);}, danger:true },
          ].map(b => (
            <TouchableOpacity key={b.label} style={[st.photoBtn, b.danger&&st.photoBtnDanger, !photoUri&&b.danger&&{opacity:0.4}]}
              onPress={b.action} disabled={b.danger&&!photoUri}>
              <Ionicons name={b.icon} size={15} color={b.danger?'#EF4444':'#0e4e96'} />
              <Text style={[st.photoBtnText, b.danger&&{color:'#EF4444'}]}>{b.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <Field label="Full Name" icon="person-outline" required value={fullName} onChange={setFullName} placeholder="John Smith" />
      <Field label="Professional Title" icon="briefcase-outline" required value={profession} onChange={setProfession} placeholder="e.g. Senior Mechanical Engineer" />
      <Field label="Email" icon="mail-outline" required value={email} onChange={setEmail} placeholder="john@example.com" keyboard="email-address" />
      <Field label="Phone" icon="call-outline" value={phone} onChange={setPhone} placeholder="+44 7700 900000" keyboard="phone-pad" />
      <Field label="Location" icon="location-outline" value={location} onChange={setLocation} placeholder="London, UK" />
      <Field label="Nationality" icon="flag-outline" value={nationality} onChange={setNationality} placeholder="British" />

      <Text style={[st.fl,{marginTop:8,marginBottom:4,color:'#0e4e96',fontWeight:'700'}]}>Online Presence</Text>
      <Field label="LinkedIn" icon="logo-linkedin" value={linkedin} onChange={setLinkedin} placeholder="linkedin.com/in/username" />
      <Field label="GitHub" icon="logo-github" value={github} onChange={setGithub} placeholder="github.com/username" />
      <Field label="Portfolio / Website" icon="globe-outline" value={portfolio} onChange={setPortfolio} placeholder="yourportfolio.com" />

      <AiTipsPanel show={aiTips} onToggle={()=>setAiTips(!aiTips)} tips={[
        'Use a professional email — ideally firstname.lastname@domain.com',
        'Your professional title should match the role you are targeting',
        'LinkedIn profile increases response rate by up to 40%',
        'Portfolio link is essential for design, engineering and dev roles',
      ]} />
    </View>
  );

  const Step2 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Professional Summary</Text>
      <Text style={st.stepDesc}>3–5 sentences: who you are, what you do, key achievements</Text>

      <View style={st.labelRow}>
        <Text style={st.fl}>Summary <Text style={{color:'#EF4444'}}>*</Text></Text>
        <TouchableOpacity style={st.aiBtn} onPress={aiGenerateSummary} disabled={generating==='summary'}>
          {generating==='summary' ? <ActivityIndicator size={14} color="#FFF" /> : <Ionicons name="flash" size={14} color="#FFF" />}
          <Text style={st.aiBtnText}>AI Generate</Text>
        </TouchableOpacity>
      </View>
      <View style={[st.fw, { alignItems:'flex-start', paddingVertical:12 }]}>
        <TextInput style={[st.fi,{minHeight:120,textAlignVertical:'top'}]}
          placeholder="Dedicated engineer with 5+ years of experience..."
          placeholderTextColor="#CBD5E1"
          value={summary} onChangeText={setSummary} multiline />
      </View>
      <View style={st.counterRow}>
        <Text style={[st.counterText, summary.length<50&&summary.length>0 && {color:'#EF4444'}]}>{summary.length} chars</Text>
        <Text style={st.counterText}>{wordCount(summary)} words</Text>
        {summary.length>0&&summary.length<80 && <Text style={{fontSize:11,color:'#F59E0B'}}>Aim for 80+ chars</Text>}
      </View>

      {aiSugs.length > 0 && (
        <View style={st.sugBox}>
          <Text style={st.sugTitle}>AI Suggestions — tap to apply</Text>
          {aiSugs.map((s,i) => (
            <TouchableOpacity key={i} style={st.sugItem} onPress={()=>{setSummary(s);setAiSugs([]);}}>
              <Text style={st.sugText}>{s}</Text>
              <Ionicons name="add-circle" size={22} color="#0e4e96" />
            </TouchableOpacity>
          ))}
          <TouchableOpacity onPress={()=>setAiSugs([])}><Text style={{color:'#EF4444',fontSize:12,textAlign:'right',marginTop:4}}>Dismiss</Text></TouchableOpacity>
        </View>
      )}

      <AiTipsPanel show={aiTips} onToggle={()=>setAiTips(!aiTips)} tips={[
        'Open with years of experience and your specialisation',
        'Mention 1–2 achievements with numbers (%, £, time saved)',
        'Tailor each summary to the specific role you are applying for',
        'Avoid first-person ("I") — start with adjective or role',
      ]} />
    </View>
  );

  const Step3 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Work Experience</Text>
      <Text style={st.stepDesc}>Add your relevant roles, most recent first</Text>

      {experiences.map((exp, idx) => (
        <View key={exp.id} style={st.card}>
          <View style={st.cardHeader}>
            <Text style={st.cardNum}>Experience {idx+1}</Text>
            <View style={{flexDirection:'row',gap:8}}>
              <TouchableOpacity style={[st.aiSmBtn, generating===`bullets_${exp.id}`&&{opacity:0.7}]}
                onPress={()=>aiEnhanceBullets(exp.id, exp.description)} disabled={generating===`bullets_${exp.id}`}>
                {generating===`bullets_${exp.id}` ? <ActivityIndicator size={12} color="#FFF" /> : <Ionicons name="flash" size={12} color="#FFF" />}
                <Text style={st.aiSmBtnText}>AI Bullets</Text>
              </TouchableOpacity>
              {experiences.length>1 && (
                <TouchableOpacity onPress={()=>delExp(exp.id)} style={st.delBtn}>
                  <Ionicons name="trash-outline" size={18} color="#EF4444" />
                </TouchableOpacity>
              )}
            </View>
          </View>

          <Field label="Job Title" icon="briefcase-outline" required value={exp.title} onChange={v=>updExp(exp.id,'title',v)} placeholder="Senior Engineer" />
          <Field label="Company" icon="business-outline" required value={exp.company} onChange={v=>updExp(exp.id,'company',v)} placeholder="Acme Engineering Ltd" />
          <Field label="Location" icon="location-outline" value={exp.location} onChange={v=>updExp(exp.id,'location',v)} placeholder="London, UK" />

          <View style={st.currentRow}>
            <Text style={st.fl}>I currently work here</Text>
            <Switch value={exp.isCurrent} onValueChange={v=>updExp(exp.id,'isCurrent',v)}
              trackColor={{false:'#D1D5DB',true:'#BFDBFE'}} thumbColor={exp.isCurrent?'#0e4e96':'#9CA3AF'} />
          </View>

          <View style={st.dateRow}>
            <View style={{flex:1}}>
              <Field label="Start Date" icon="calendar-outline" value={exp.startDate} onChange={v=>updExp(exp.id,'startDate',v)} placeholder="Jan 2020" />
            </View>
            <View style={{flex:1,marginLeft:12}}>
              <Field label="End Date" icon="calendar-outline" value={exp.isCurrent?'Present':exp.endDate}
                onChange={v=>updExp(exp.id,'endDate',v)} placeholder="Present" />
            </View>
          </View>

          <View style={st.fg}>
            <Text style={st.fl}>Description</Text>
            <View style={[st.fw,{alignItems:'flex-start',paddingVertical:12}]}>
              <TextInput style={[st.fi,{minHeight:90,textAlignVertical:'top'}]}
                placeholder="Key responsibilities, technologies used, team size..."
                placeholderTextColor="#CBD5E1"
                value={exp.description} onChangeText={v=>updExp(exp.id,'description',v)} multiline />
            </View>
            <Text style={st.counterText}>{wordCount(exp.description)} words</Text>
          </View>

          <View style={st.fg}>
            <Text style={st.fl}>Key Achievements</Text>
            <View style={[st.fw,{alignItems:'flex-start',paddingVertical:12}]}>
              <TextInput style={[st.fi,{minHeight:70,textAlignVertical:'top'}]}
                placeholder="• Reduced delivery time by 25%&#10;• Led team of 8 engineers&#10;• Saved £150k annually"
                placeholderTextColor="#CBD5E1"
                value={exp.achievements} onChangeText={v=>updExp(exp.id,'achievements',v)} multiline />
            </View>
          </View>

          {/* AI bullet suggestions */}
          {aiBulletSugs.expId===exp.id && aiBulletSugs.items.length>0 && (
            <View style={st.sugBox}>
              <Text style={st.sugTitle}>AI Bullet Suggestions</Text>
              {aiBulletSugs.items.map((b,i) => (
                <TouchableOpacity key={i} style={st.sugItem} onPress={()=>{
                  updExp(exp.id,'achievements',(exp.achievements?exp.achievements+'\n':'')+b);
                  setAiBulletSugs({expId:null,items:[]});
                }}>
                  <Text style={[st.sugText,{flex:1}]}>{b}</Text>
                  <Ionicons name="add-circle" size={20} color="#0e4e96" />
                </TouchableOpacity>
              ))}
              <TouchableOpacity onPress={()=>setAiBulletSugs({expId:null,items:[]})}>
                <Text style={{color:'#EF4444',fontSize:12,textAlign:'right',marginTop:4}}>Dismiss</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      ))}

      <TouchableOpacity style={st.addBtn} onPress={addExp}>
        <Ionicons name="add-circle-outline" size={22} color="#0e4e96" />
        <Text style={st.addBtnText}>Add Another Experience</Text>
      </TouchableOpacity>

      <AiTipsPanel show={aiTips} onToggle={()=>setAiTips(!aiTips)} tips={[
        'Use action verbs: Led, Built, Improved, Reduced, Delivered',
        'Quantify results: percentages, £ values, time savings',
        'Focus on impact, not just responsibilities',
        'Add 3–5 bullet points per role for best ATS performance',
      ]} />
    </View>
  );

  const Step4 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Education</Text>
      <Text style={st.stepDesc}>Add your academic qualifications, most recent first</Text>

      {education.map((edu, idx) => (
        <View key={edu.id} style={st.card}>
          <View style={st.cardHeader}>
            <Text style={st.cardNum}>Education {idx+1}</Text>
            {education.length>1 && (
              <TouchableOpacity onPress={()=>delEdu(edu.id)} style={st.delBtn}>
                <Ionicons name="trash-outline" size={18} color="#EF4444" />
              </TouchableOpacity>
            )}
          </View>
          <Field label="Degree" icon="school-outline" required value={edu.degree} onChange={v=>updEdu(edu.id,'degree',v)} placeholder="BEng Mechanical Engineering" />
          <Field label="Institution" icon="library-outline" required value={edu.institution} onChange={v=>updEdu(edu.id,'institution',v)} placeholder="University of Manchester" />
          <Field label="Location" icon="location-outline" value={edu.location} onChange={v=>updEdu(edu.id,'location',v)} placeholder="Manchester, UK" />

          <View style={st.dateRow}>
            <View style={{flex:1}}><Field label="Start Date" icon="calendar-outline" value={edu.startDate} onChange={v=>updEdu(edu.id,'startDate',v)} placeholder="Sep 2017" /></View>
            <View style={{flex:1,marginLeft:12}}><Field label="End Date" icon="calendar-outline" value={edu.endDate} onChange={v=>updEdu(edu.id,'endDate',v)} placeholder="Jun 2021" /></View>
          </View>

          <View style={st.dateRow}>
            <View style={{flex:1}}><Field label="GPA / Grade" icon="ribbon-outline" value={edu.gpa} onChange={v=>updEdu(edu.id,'gpa',v)} placeholder="3.8 / First Class" /></View>
            <View style={{flex:1,marginLeft:12}}><Field label="Honors / Awards" icon="trophy-outline" value={edu.honors} onChange={v=>updEdu(edu.id,'honors',v)} placeholder="Dean's List" /></View>
          </View>

          <Field label="Relevant Modules / Notes" value={edu.description} onChange={v=>updEdu(edu.id,'description',v)}
            placeholder="Thermodynamics, Fluid Mechanics, FEA, Structural Analysis…" multiline />
        </View>
      ))}

      <TouchableOpacity style={st.addBtn} onPress={addEdu}>
        <Ionicons name="add-circle-outline" size={22} color="#0e4e96" />
        <Text style={st.addBtnText}>Add Another Education</Text>
      </TouchableOpacity>

      <AiTipsPanel show={aiTips} onToggle={()=>setAiTips(!aiTips)} tips={[
        'List highest degree first',
        'Include GPA if 3.5+ or First Class',
        'Add relevant modules for entry-level roles',
        'Include professional courses, bootcamps, and MOOCs',
      ]} />
    </View>
  );

  const Step5 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Skills</Text>
      <Text style={st.stepDesc}>Add technical and soft skills with proficiency level</Text>

      <View style={st.fg}>
        <Text style={st.fl}>Add Skill</Text>
        <View style={{flexDirection:'row',gap:8,marginBottom:8}}>
          <View style={[st.fw,{flex:1}]}>
            <Ionicons name="construct-outline" size={18} color="#94a3b8" style={{marginRight:10}} />
            <TextInput style={st.fi} placeholder="e.g. Python, SolidWorks, Agile"
              placeholderTextColor="#CBD5E1" value={skillInput} onChangeText={setSkillInput}
              onSubmitEditing={addSkill} returnKeyType="done" />
          </View>
          <TouchableOpacity style={[st.addSkillBtn,{opacity:skillInput.trim()?1:0.4}]} onPress={addSkill} disabled={!skillInput.trim()}>
            <Ionicons name="add" size={22} color="#FFF" />
          </TouchableOpacity>
        </View>

        {/* Level selector */}
        <View style={{flexDirection:'row',gap:6,flexWrap:'wrap',marginBottom:12}}>
          {SKILL_LEVELS.map(lvl => (
            <TouchableOpacity key={lvl} style={[st.levelChip, skillLevel===lvl && {backgroundColor:SKILL_LEVEL_COLORS[lvl],borderColor:SKILL_LEVEL_COLORS[lvl]}]}
              onPress={()=>setSkillLevel(lvl)}>
              <Text style={[st.levelChipText, skillLevel===lvl&&{color:'#FFF'}]}>{lvl}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Suggested skills */}
      {profession && (
        <View style={st.suggestRow}>
          <Text style={st.suggestLabel}>Suggested for your role:</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {(ATS_KEYWORDS[Object.keys(ATS_KEYWORDS).find(k=>profession.toLowerCase().includes(k))||'software']||[]).filter(k=>!skills.some(s=>(s.name||s)===k)).slice(0,8).map(kw => (
              <TouchableOpacity key={kw} style={st.suggestChip} onPress={()=>setSkills(prev=>[...prev,{name:kw,level:skillLevel}])}>
                <Text style={st.suggestChipText}>+ {kw}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {skills.length > 0 ? (
        <View style={{marginTop:12}}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <Text style={st.fl}>Your Skills ({skills.length})</Text>
            <TouchableOpacity onPress={()=>Alert.alert('Clear All','Remove all skills?',[{text:'Cancel',style:'cancel'},{text:'Clear',style:'destructive',onPress:()=>setSkills([])}])}>
              <Text style={{fontSize:12,color:'#EF4444',fontWeight:'600'}}>Clear All</Text>
            </TouchableOpacity>
          </View>
          {skills.map((skill, idx) => {
            const name = skill.name||skill; const lvl = skill.level||'Intermediate';
            return (
              <View key={idx} style={st.skillRow}>
                <View style={[st.skillLvlDot,{backgroundColor:SKILL_LEVEL_COLORS[lvl]||'#94a3b8'}]} />
                <Text style={st.skillName}>{name}</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{flex:1}}>
                  <View style={{flexDirection:'row',gap:4}}>
                    {SKILL_LEVELS.map(l => (
                      <TouchableOpacity key={l} style={[st.skillLvlBtn,lvl===l&&{backgroundColor:SKILL_LEVEL_COLORS[l],borderColor:SKILL_LEVEL_COLORS[l]}]}
                        onPress={()=>updSkillLevel(idx,l)}>
                        <Text style={[st.skillLvlBtnText,lvl===l&&{color:'#FFF'}]}>{l}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </ScrollView>
                <TouchableOpacity onPress={()=>delSkill(idx)} style={{padding:4}}>
                  <Ionicons name="close-circle" size={20} color="#EF4444" />
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      ) : (
        <View style={st.emptyBox}>
          <Ionicons name="construct-outline" size={44} color="#CBD5E1" />
          <Text style={st.emptyText}>No skills added yet</Text>
          <Text style={st.emptySub}>Tap the suggested skills above or type your own</Text>
        </View>
      )}

      <AiTipsPanel show={aiTips} onToggle={()=>setAiTips(!aiTips)} tips={[
        'Aim for 8–15 skills — quality over quantity',
        'Match skills exactly to job description keywords',
        'Separate technical skills from soft skills',
        'Be specific: "SolidWorks 2023" beats "CAD software"',
      ]} />
    </View>
  );

  const Step6 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Extra Sections</Text>
      <Text style={st.stepDesc}>Certifications, projects, languages & more</Text>

      {/* Certifications */}
      <SectionBlock title="Certifications" icon="ribbon-outline" items={certifications} onAdd={addCert}
        renderItem={(c) => (
          <View key={c.id} style={st.extraCard}>
            <View style={{flexDirection:'row',justifyContent:'flex-end'}}><TouchableOpacity onPress={()=>delCert(c.id)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity></View>
            <Field label="Certificate Name" value={c.name} onChange={v=>updCert(c.id,'name',v)} placeholder="AWS Solutions Architect" />
            <Field label="Issuer" value={c.issuer} onChange={v=>updCert(c.id,'issuer',v)} placeholder="Amazon Web Services" />
            <View style={st.dateRow}>
              <View style={{flex:1}}><Field label="Issue Date" value={c.date} onChange={v=>updCert(c.id,'date',v)} placeholder="Jan 2024" /></View>
              <View style={{flex:1,marginLeft:12}}><Field label="Expiry" value={c.expiry} onChange={v=>updCert(c.id,'expiry',v)} placeholder="Jan 2027 / None" /></View>
            </View>
            <Field label="Credential ID" value={c.credId} onChange={v=>updCert(c.id,'credId',v)} placeholder="ABC123456" />
          </View>
        )}
      />

      {/* Projects */}
      <SectionBlock title="Projects" icon="code-slash-outline" items={projects} onAdd={addProj}
        renderItem={(p) => (
          <View key={p.id} style={st.extraCard}>
            <View style={{flexDirection:'row',justifyContent:'flex-end'}}><TouchableOpacity onPress={()=>delProj(p.id)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity></View>
            <Field label="Project Name" value={p.name} onChange={v=>updProj(p.id,'name',v)} placeholder="Smart Energy Monitor" />
            <Field label="Technologies" value={p.tech} onChange={v=>updProj(p.id,'tech',v)} placeholder="Python, React, PostgreSQL" />
            <Field label="URL / GitHub" value={p.url} onChange={v=>updProj(p.id,'url',v)} placeholder="github.com/user/project" />
            <Field label="Description" value={p.description} onChange={v=>updProj(p.id,'description',v)} placeholder="What it does and your key contributions..." multiline />
          </View>
        )}
      />

      {/* Languages */}
      <SectionBlock title="Languages" icon="chatbubble-outline" items={languages} onAdd={addLang}
        renderItem={(l) => (
          <View key={l.id} style={[st.extraCard,{flexDirection:'row',alignItems:'center',gap:12}]}>
            <View style={{flex:1}}><Field label="Language" value={l.name} onChange={v=>updLang(l.id,'name',v)} placeholder="French" /></View>
            <View style={{flex:1}}>
              <Text style={[st.fl,{marginBottom:6}]}>Level</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {LANG_LEVELS.map(lv => (
                  <TouchableOpacity key={lv} style={[st.levelChip,l.level===lv&&{backgroundColor:'#0e4e96',borderColor:'#0e4e96'}]} onPress={()=>updLang(l.id,'level',lv)}>
                    <Text style={[st.levelChipText,l.level===lv&&{color:'#FFF'}]}>{lv}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
            <TouchableOpacity onPress={()=>delLang(l.id)} style={{padding:4}}>
              <Ionicons name="trash-outline" size={16} color="#EF4444" />
            </TouchableOpacity>
          </View>
        )}
      />

      {/* Awards */}
      <SectionBlock title="Awards & Recognition" icon="trophy-outline" items={awards} onAdd={addAward}
        renderItem={(a) => (
          <View key={a.id} style={st.extraCard}>
            <View style={{flexDirection:'row',justifyContent:'flex-end'}}><TouchableOpacity onPress={()=>delAward(a.id)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity></View>
            <Field label="Award Title" value={a.title} onChange={v=>updAward(a.id,'title',v)} placeholder="Engineer of the Year" />
            <View style={st.dateRow}>
              <View style={{flex:1}}><Field label="Issuer" value={a.issuer} onChange={v=>updAward(a.id,'issuer',v)} placeholder="Institution of Engineering" /></View>
              <View style={{flex:1,marginLeft:12}}><Field label="Date" value={a.date} onChange={v=>updAward(a.id,'date',v)} placeholder="2023" /></View>
            </View>
            <Field label="Description" value={a.description} onChange={v=>updAward(a.id,'description',v)} placeholder="Brief context..." multiline />
          </View>
        )}
      />

      {/* Volunteer */}
      <SectionBlock title="Volunteer Work" icon="heart-outline" items={volunteer} onAdd={addVol}
        renderItem={(v2) => (
          <View key={v2.id} style={st.extraCard}>
            <View style={{flexDirection:'row',justifyContent:'flex-end'}}><TouchableOpacity onPress={()=>delVol(v2.id)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity></View>
            <Field label="Role" value={v2.role} onChange={v=>updVol(v2.id,'role',v)} placeholder="Volunteer Engineer" />
            <Field label="Organisation" value={v2.org} onChange={v=>updVol(v2.id,'org',v)} placeholder="Engineers Without Borders" />
            <View style={st.dateRow}>
              <View style={{flex:1}}><Field label="Start Date" value={v2.startDate} onChange={v=>updVol(v2.id,'startDate',v)} placeholder="Jan 2022" /></View>
              <View style={{flex:1,marginLeft:12}}><Field label="End Date" value={v2.endDate} onChange={v=>updVol(v2.id,'endDate',v)} placeholder="Present" /></View>
            </View>
            <Field label="Description" value={v2.description} onChange={v=>updVol(v2.id,'description',v)} placeholder="What you did and impact..." multiline />
          </View>
        )}
      />
    </View>
  );

  const Step7 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Choose Template</Text>
      <Text style={st.stepDesc}>Select a style that fits your industry</Text>

      <View style={{flexDirection:'row',flexWrap:'wrap',gap:14}}>
        {TEMPLATES.map(tpl => (
          <TouchableOpacity key={tpl.id} style={[st.tplCard, selectedTemplate===tpl.id && st.tplCardSelected]}
            onPress={()=>setSelectedTemplate(tpl.id)}>
            {/* Mini template preview */}
            <View style={[st.tplPreview, {backgroundColor:tpl.bg}]}>
              <View style={{height:10,backgroundColor:tpl.accent,borderRadius:2,marginBottom:6}} />
              <View style={{height:6,backgroundColor:tpl.accent,opacity:0.4,borderRadius:1,marginBottom:3,width:'80%'}} />
              <View style={{height:4,backgroundColor:'#E2E8F0',borderRadius:1,marginBottom:3}} />
              <View style={{height:4,backgroundColor:'#E2E8F0',borderRadius:1,marginBottom:3,width:'70%'}} />
              <View style={{height:4,backgroundColor:'#E2E8F0',borderRadius:1,width:'90%'}} />
            </View>
            <Text style={st.tplName}>{tpl.name}</Text>
            <Text style={st.tplDesc}>{tpl.desc}</Text>
            {selectedTemplate===tpl.id && (
              <View style={st.tplCheck}><Ionicons name="checkmark-circle" size={22} color="#19abb5" /></View>
            )}
          </TouchableOpacity>
        ))}
      </View>

      <TouchableOpacity style={st.previewBtn} onPress={()=>setPreviewModal(true)}>
        <Ionicons name="eye-outline" size={18} color="#0e4e96" />
        <Text style={st.previewBtnText}>Live Preview</Text>
      </TouchableOpacity>

      <AiTipsPanel show={aiTips} onToggle={()=>setAiTips(!aiTips)} tips={[
        'Modern: best for tech, engineering and startup roles',
        'Professional: ideal for finance, legal, and corporate',
        'Creative: stands out for design and marketing positions',
        'Technical: optimised for developer and engineering CVs',
        'Executive: used for director and C-suite applications',
      ]} />
    </View>
  );

  const Step8 = () => (
    <View style={st.step}>
      <Text style={st.stepTitle}>Job Match & ATS Test</Text>
      <Text style={st.stepDesc}>Paste a job description to see how well your resume matches</Text>

      <View style={st.fg}>
        <Text style={st.fl}>Paste Job Description</Text>
        <View style={[st.fw,{alignItems:'flex-start',paddingVertical:12}]}>
          <TextInput style={[st.fi,{minHeight:140,textAlignVertical:'top'}]}
            placeholder="Paste the full job description here to get a personalised match score and missing keyword recommendations..."
            placeholderTextColor="#CBD5E1" value={jobDesc} onChangeText={setJobDesc} multiline />
        </View>
        <Text style={st.counterText}>{wordCount(jobDesc)} words</Text>
      </View>

      <TouchableOpacity style={[st.analyseBtn,{backgroundColor:'#19abb5'}]} onPress={matchToJD} disabled={!jobDesc.trim()}>
        <MaterialCommunityIcons name="target" size={18} color="#FFF" />
        <Text style={st.analyseBtnText}>Match My Resume</Text>
      </TouchableOpacity>

      {jdMatch && (
        <View style={[st.card,{marginTop:16}]}>
          <Text style={st.cardNum}>Job Match Score</Text>
          <View style={st.scoreRingRow}>
            <View style={[st.scoreRing,{borderColor:scoreColor(jdMatch.score)}]}>
              <Text style={[st.scoreRingVal,{color:scoreColor(jdMatch.score)}]}>{jdMatch.score}%</Text>
              <Text style={st.scoreRingLabel}>Match</Text>
            </View>
            <View style={{flex:1,marginLeft:16}}>
              <Text style={st.fl}>Keywords matched: <Text style={{color:'#10B981',fontWeight:'700'}}>{jdMatch.matched} / {jdMatch.total}</Text></Text>
              {jdMatch.missing.length>0 && (
                <>
                  <Text style={[st.fl,{marginTop:10,color:'#F97316'}]}>Missing keywords:</Text>
                  <View style={{flexDirection:'row',flexWrap:'wrap',gap:4,marginTop:4}}>
                    {jdMatch.missing.map((k,i)=>(
                      <TouchableOpacity key={i} style={st.missingKw} onPress={()=>setSkills(prev=>[...prev,{name:k.charAt(0).toUpperCase()+k.slice(1),level:'Intermediate'}])}>
                        <Text style={st.missingKwText}>+ {k}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <Text style={{fontSize:10,color:'#94a3b8',marginTop:4}}>Tap + to add as skill</Text>
                </>
              )}
            </View>
          </View>
        </View>
      )}

      {atsResult && (
        <View style={[st.card,{marginTop:12}]}>
          <Text style={st.cardNum}>ATS Compatibility</Text>
          <View style={st.atsScoreRow}>
            <View style={[st.atsScoreBig,{borderColor:scoreColor(atsResult.score)}]}>
              <Text style={[st.atsScoreVal,{color:scoreColor(atsResult.score)}]}>{atsResult.score}%</Text>
              <Text style={st.atsScoreLabel}>ATS Score</Text>
            </View>
            <View style={{flex:1,marginLeft:16,gap:8}}>
              {[
                {label:'Keyword Match', val:atsResult.kwScore},
                {label:'Formatting',    val:atsResult.fmtScore},
                {label:'Sections',      val:atsResult.secScore},
              ].map(m=>(
                <View key={m.label}>
                  <View style={{flexDirection:'row',justifyContent:'space-between'}}>
                    <Text style={{fontSize:11,color:'#6B7280'}}>{m.label}</Text>
                    <Text style={{fontSize:11,fontWeight:'700',color:scoreColor(m.val)}}>{m.val}%</Text>
                  </View>
                  <View style={st.atsBar}><View style={[st.atsBarFill,{width:`${m.val}%`,backgroundColor:scoreColor(m.val)}]} /></View>
                </View>
              ))}
            </View>
          </View>
          {atsResult.recommendations.map((r,i)=>(
            <View key={i} style={st.recRow}>
              <MaterialCommunityIcons name="lightbulb-outline" size={14} color="#F59E0B" />
              <Text style={st.recText}>{r}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const Step9 = () => {
    const score = calcScore();
    return (
      <View style={st.step}>
        <Text style={st.stepTitle}>Preview & Export</Text>
        <Text style={st.stepDesc}>Review, analyse, and download your resume</Text>

        {/* Score ring */}
        <View style={st.card}>
          <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
            <Text style={st.cardNum}>Resume Completeness</Text>
            <TouchableOpacity style={[st.analyseBtn,{paddingHorizontal:14,paddingVertical:8}]} onPress={aiAnalyse} disabled={generating==='analyse'}>
              {generating==='analyse' ? <ActivityIndicator size={14} color="#FFF" /> : <Ionicons name="analytics-outline" size={14} color="#FFF" />}
              <Text style={[st.analyseBtnText,{fontSize:12}]}>Analyse with AI</Text>
            </TouchableOpacity>
          </View>
          <View style={st.scoreRingRow}>
            <View style={[st.scoreRing,{borderColor:scoreColor(score),width:90,height:90,borderRadius:45}]}>
              <Text style={[st.scoreRingVal,{color:scoreColor(score),fontSize:26}]}>{score}%</Text>
            </View>
            <View style={{flex:1,marginLeft:16}}>
              <View style={st.checkItem}><Ionicons name={fullName&&profession&&email?'checkmark-circle':'ellipse-outline'} size={16} color={fullName&&profession&&email?'#10B981':'#CBD5E1'} /><Text style={st.checkLabel}>Personal info complete</Text></View>
              <View style={st.checkItem}><Ionicons name={summary.length>=80?'checkmark-circle':'ellipse-outline'} size={16} color={summary.length>=80?'#10B981':'#CBD5E1'} /><Text style={st.checkLabel}>Strong summary</Text></View>
              <View style={st.checkItem}><Ionicons name={experiences.filter(e=>e.title&&e.company).length>0?'checkmark-circle':'ellipse-outline'} size={16} color={experiences.filter(e=>e.title).length>0?'#10B981':'#CBD5E1'} /><Text style={st.checkLabel}>Work experience added</Text></View>
              <View style={st.checkItem}><Ionicons name={skills.length>=5?'checkmark-circle':'ellipse-outline'} size={16} color={skills.length>=5?'#10B981':'#CBD5E1'} /><Text style={st.checkLabel}>5+ skills listed</Text></View>
              <View style={st.checkItem}><Ionicons name={linkedin?'checkmark-circle':'ellipse-outline'} size={16} color={linkedin?'#10B981':'#CBD5E1'} /><Text style={st.checkLabel}>LinkedIn added</Text></View>
            </View>
          </View>
        </View>

        {/* AI feedback */}
        {aiFeedback.length > 0 && (
          <View style={st.card}>
            <Text style={st.cardNum}>AI Feedback</Text>
            {aiFeedback.map((fb,i) => (
              <View key={i} style={[st.fbRow, fb.type==='positive'&&{backgroundColor:'#F0FDF4'}, fb.type==='improvement'&&{backgroundColor:'#FFFBEB'}]}>
                <Ionicons name={fb.type==='positive'?'checkmark-circle':fb.type==='keyword'?'search-outline':'information-circle'}
                  size={18} color={fb.type==='positive'?'#10B981':fb.type==='keyword'?'#0e4e96':'#F59E0B'} />
                <Text style={st.fbText}>{fb.message}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Export actions */}
        <View style={st.card}>
          <Text style={st.cardNum}>Export Options</Text>
          {[
            { label:'Preview Resume',     icon:'eye-outline',            color:'#0e4e96',  action:()=>setPreviewModal(true)        },
            { label:'Sync to Profile',     icon:'sync-outline',           color:'#10B981',  action:()=>syncToProfile(true)          },
            { label:'Use for Job Applications', icon:'briefcase-outline',  color:'#0e4e96',  action:saveToApplications               },
            { label:'Save to My Docs',     icon:'folder-outline',         color:'#059669',  action:saveResumeToDocuments             },
            { label:'Generate PDF',        icon:'download-outline',       color:'#10B981',  action:generatePdf                      },
            { label:'Generate Cover Letter',icon:'document-text-outline', color:'#8B5CF6',  action:aiGenerateCoverLetter            },
            { label:'Save to Profile',     icon:'cloud-upload-outline',   color:'#19abb5',  action:saveToApi                        },
            { label:'Save Version',        icon:'bookmark-outline',       color:'#F59E0B',  action:()=>Alert.prompt('Version Name','Name this version:',(n)=>saveVersion(n||undefined)) },
            { label:'Load Version',        icon:'folder-open-outline',    color:'#6B7280',  action:()=>setVersionModal(true)        },
            { label:'View in Profile',      icon:'person-circle-outline',  color:'#6D28D9',  action:async()=>{await syncToProfile(false);navigation.navigate('Profile');} }
          ].map(e => (
            <TouchableOpacity key={e.label} style={[st.exportRow,{borderLeftColor:e.color}]} onPress={e.action}
              disabled={(e.label==='Generate PDF'||e.label==='Generate Cover Letter')&&(isGenerating||generating==='cover')}>
              <View style={[st.exportIcon,{backgroundColor:e.color+'22'}]}>
                {(e.label==='Generate PDF'&&isGenerating)||(e.label==='Generate Cover Letter'&&generating==='cover')
                  ? <ActivityIndicator size={18} color={e.color} />
                  : <Ionicons name={e.icon} size={20} color={e.color} />
                }
              </View>
              <Text style={st.exportLabel}>{e.label}</Text>
              <Ionicons name="chevron-forward" size={16} color="#CBD5E1" />
            </TouchableOpacity>
          ))}
        </View>

        {(!Print||!Sharing) && <Text style={st.installHint}>Install for PDF: npx expo install expo-print expo-sharing</Text>}
      </View>
    );
  };

  // ─── Preview Modal ────────────────────────────────────────────────────────
  const renderPreviewModal = () => (
    <Modal animationType="slide" transparent visible={previewModal} onRequestClose={()=>setPreviewModal(false)}>
      <View style={st.modalOverlay}>
        <View style={[st.modalSheet,{maxHeight:height*0.92}]}>
          <View style={st.modalHdr}>
            <Text style={st.modalTitle}>Resume Preview</Text>
            <TouchableOpacity onPress={()=>setPreviewModal(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>

          <ScrollView style={st.previewScroll}>
            {/* Header */}
            <LinearGradientFallback accent={tpl.accent} bg={tpl.bg}>
              <View style={{flexDirection:'row',alignItems:'center',padding:20,gap:14}}>
                {photoUri
                  ? <Image source={{uri:photoUri}} style={{width:70,height:70,borderRadius:35,borderWidth:2,borderColor:'#FFF'}} />
                  : <View style={{width:70,height:70,borderRadius:35,backgroundColor:tpl.accent+'44',justifyContent:'center',alignItems:'center'}}>
                      <Text style={{fontSize:28,color:tpl.accent,fontWeight:'800'}}>{fullName?.charAt(0)||'?'}</Text>
                    </View>
                }
                <View style={{flex:1}}>
                  <Text style={{fontSize:22,fontWeight:'900',color:'#0f172a'}}>{fullName||'Your Name'}</Text>
                  <Text style={{fontSize:14,fontWeight:'700',color:tpl.accent,marginTop:2}}>{profession||'Professional Title'}</Text>
                  <Text style={{fontSize:12,color:'#64748b',marginTop:4}}>{[location,email,phone].filter(Boolean).join('  ·  ')}</Text>
                  {(linkedin||github) && (
                    <Text style={{fontSize:11,color:tpl.accent,marginTop:2}}>{[linkedin,github].filter(Boolean).join('  ·  ')}</Text>
                  )}
                </View>
              </View>
            </LinearGradientFallback>

            <View style={{padding:16}}>
              {/* Summary */}
              {summary ? <PreviewSection title="Professional Summary" accent={tpl.accent}>
                <Text style={{fontSize:13,color:'#374151',lineHeight:20}}>{summary}</Text>
              </PreviewSection> : null}

              {/* Skills */}
              {skills.length>0 && <PreviewSection title="Skills" accent={tpl.accent}>
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:6}}>
                  {skills.map((s,i)=>{
                    const n=s.name||s; const lc=SKILL_LEVEL_COLORS[s.level]||tpl.accent;
                    return <View key={i} style={{backgroundColor:lc+'22',borderRadius:16,paddingHorizontal:10,paddingVertical:4,borderWidth:1,borderColor:lc+'66'}}>
                      <Text style={{fontSize:11,fontWeight:'600',color:lc}}>{n}</Text>
                    </View>;
                  })}
                </View>
              </PreviewSection>}

              {/* Experience */}
              {experiences.some(e=>e.title||e.company) && <PreviewSection title="Experience" accent={tpl.accent}>
                {experiences.filter(e=>e.title||e.company).map((e,i)=>(
                  <View key={i} style={{marginBottom:14}}>
                    <Text style={{fontWeight:'800',fontSize:14,color:'#0f172a'}}>{e.title}</Text>
                    <Text style={{fontSize:12,color:'#64748b',marginTop:1}}>{e.company}{e.location?` · ${e.location}`:''} {e.startDate?`| ${e.startDate}–${e.isCurrent?'Present':e.endDate}`:''}</Text>
                    {e.description?<Text style={{fontSize:12,color:'#374151',marginTop:4,lineHeight:18}}>{e.description}</Text>:null}
                    {e.achievements?<Text style={{fontSize:12,color:'#0f172a',fontWeight:'600',marginTop:4}}>🏆 {e.achievements}</Text>:null}
                  </View>
                ))}
              </PreviewSection>}

              {/* Education */}
              {education.some(e=>e.degree||e.institution) && <PreviewSection title="Education" accent={tpl.accent}>
                {education.filter(e=>e.degree||e.institution).map((e,i)=>(
                  <View key={i} style={{marginBottom:10}}>
                    <Text style={{fontWeight:'800',fontSize:14,color:'#0f172a'}}>{e.degree}</Text>
                    <Text style={{fontSize:12,color:'#64748b',marginTop:1}}>{e.institution}{e.gpa?` · GPA: ${e.gpa}`:''}</Text>
                    {e.honors?<Text style={{fontSize:11,color:'#374151',marginTop:2}}>{e.honors}</Text>:null}
                  </View>
                ))}
              </PreviewSection>}

              {/* Certs */}
              {certifications.filter(c=>c.name).length>0 && <PreviewSection title="Certifications" accent={tpl.accent}>
                {certifications.filter(c=>c.name).map((c,i)=>(
                  <View key={i} style={{marginBottom:6}}>
                    <Text style={{fontSize:13,fontWeight:'700'}}>{c.name}</Text>
                    <Text style={{fontSize:12,color:'#64748b'}}>{c.issuer}{c.date?` | ${c.date}`:''}</Text>
                  </View>
                ))}
              </PreviewSection>}

              {/* Languages */}
              {languages.filter(l=>l.name).length>0 && <PreviewSection title="Languages" accent={tpl.accent}>
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
                  {languages.filter(l=>l.name).map((l,i)=>(
                    <View key={i} style={{backgroundColor:'#F1F5F9',borderRadius:14,paddingHorizontal:10,paddingVertical:4}}>
                      <Text style={{fontSize:12,fontWeight:'600'}}>{l.name} <Text style={{color:'#64748b',fontWeight:'400'}}>({l.level})</Text></Text>
                    </View>
                  ))}
                </View>
              </PreviewSection>}
            </View>
          </ScrollView>

          <View style={st.modalFooter}>
            <TouchableOpacity style={st.modalClosBtn} onPress={()=>setPreviewModal(false)}>
              <Text style={st.modalClosBtnText}>Close</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.modalClosBtn,{backgroundColor:'#0e4e96',flex:2}]} onPress={generatePdf} disabled={isGenerating}>
              {isGenerating ? <ActivityIndicator size={16} color="#FFF" /> : <><Ionicons name="download-outline" size={16} color="#FFF" /><Text style={[st.modalClosBtnText,{color:'#FFF',marginLeft:6}]}>Download PDF</Text></>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── Cover Letter Modal ───────────────────────────────────────────────────
  const renderCoverModal = () => (
    <Modal animationType="slide" transparent visible={coverModal} onRequestClose={()=>setCoverModal(false)}>
      <View style={st.modalOverlay}>
        <View style={[st.modalSheet,{maxHeight:height*0.85}]}>
          <View style={st.modalHdr}>
            <Text style={st.modalTitle}>Cover Letter</Text>
            <TouchableOpacity onPress={()=>setCoverModal(false)}><Ionicons name="close" size={24} color="#64748b" /></TouchableOpacity>
          </View>
          <ScrollView style={{padding:16}}>
            <TextInput style={[st.fw,{minHeight:300,fontSize:13,padding:14,color:'#0f172a',lineHeight:22,textAlignVertical:'top'}]}
              multiline value={coverLetter} onChangeText={setCoverLetter} />
          </ScrollView>
          <View style={st.modalFooter}>
            <TouchableOpacity style={st.modalClosBtn} onPress={()=>setCoverModal(false)}>
              <Text style={st.modalClosBtnText}>Close</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.modalClosBtn,{backgroundColor:'#19abb5',flex:2}]}
              onPress={async()=>{
                if (!Sharing) { Alert.alert('Install','npx expo install expo-sharing'); return; }
                if (!Print)   { Alert.alert('Install','npx expo install expo-print');   return; }
                const html=`<html><body style="font-family:Arial;padding:32px;line-height:1.7;white-space:pre-wrap;">${escapeHtml(coverLetter)}</body></html>`;
                const f = await Print.printToFileAsync({html,base64:false});
                await Sharing.shareAsync(f.uri,{mimeType:'application/pdf'});
              }}>
              <Ionicons name="download-outline" size={16} color="#FFF" />
              <Text style={[st.modalClosBtnText,{color:'#FFF',marginLeft:6}]}>Export PDF</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── Version Modal ────────────────────────────────────────────────────────
  const renderVersionModal = () => (
    <Modal animationType="slide" transparent visible={versionModal} onRequestClose={()=>setVersionModal(false)}>
      <View style={st.modalOverlay}>
        <View style={[st.modalSheet,{maxHeight:height*0.7}]}>
          <View style={st.modalHdr}>
            <Text style={st.modalTitle}>Saved Versions</Text>
            <TouchableOpacity onPress={()=>setVersionModal(false)}><Ionicons name="close" size={24} color="#64748b" /></TouchableOpacity>
          </View>
          {versions.length===0
            ? <View style={st.emptyBox}><Ionicons name="folder-open-outline" size={48} color="#CBD5E1" /><Text style={st.emptyText}>No versions saved yet</Text></View>
            : <FlatList data={versions} keyExtractor={i=>String(i.id)}
                contentContainerStyle={{padding:16}}
                renderItem={({item})=>(
                  <TouchableOpacity style={st.verRow} onPress={()=>loadVersion(item)}>
                    <Ionicons name="document-text-outline" size={22} color="#0e4e96" />
                    <View style={{flex:1,marginLeft:12}}>
                      <Text style={{fontSize:14,fontWeight:'700',color:'#0f172a'}}>{item.name}</Text>
                      <Text style={{fontSize:12,color:'#94a3b8'}}>{new Date(item.savedAt).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</Text>
                    </View>
                    <TouchableOpacity onPress={()=>{
                      Alert.alert('Delete','Delete this version?',[{text:'Cancel',style:'cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
                        const upd=versions.filter(v=>v.id!==item.id);setVersions(upd);await AsyncStorage.setItem(VERS_KEY,JSON.stringify(upd));
                      }}]);
                    }}>
                      <Ionicons name="trash-outline" size={18} color="#EF4444" />
                    </TouchableOpacity>
                  </TouchableOpacity>
                )}
              />
          }
        </View>
      </View>
    </Modal>
  );

  // ─── Step content router ──────────────────────────────────────────────────
  const renderStep = () => {
    switch (activeStep) {
      case 1: return Step1();
      case 2: return Step2();
      case 3: return Step3();
      case 4: return Step4();
      case 5: return Step5();
      case 6: return Step6();
      case 7: return Step7();
      case 8: return Step8();
      case 9: return Step9();
      default: return Step1();
    }
  };

  // ─── Root render ──────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={st.root}>
      <StatusBar barStyle="dark-content" backgroundColor="#FFF" />

      {/* Header */}
      <View style={st.header}>
        <TouchableOpacity style={st.backBtn} onPress={()=>navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#0f172a" />
        </TouchableOpacity>
        <View style={{alignItems:'center'}}>
          <Text style={st.headerTitle}>Build Resume</Text>
          <Text style={st.headerSub}>{STEPS[activeStep-1]?.label} · Step {activeStep}/{STEPS.length}</Text>
        </View>
        <TouchableOpacity style={st.backBtn} onPress={()=>setVersionModal(true)}>
          <Ionicons name="folder-open-outline" size={22} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* Step progress dots */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.stepsBar} contentContainerStyle={{alignItems:'center',paddingHorizontal:12}}>
        {STEPS.map((step,idx) => (
          <TouchableOpacity key={step.id} style={st.stepDotWrap} onPress={()=>setActiveStep(step.id)}>
            <View style={[st.stepDot, activeStep===step.id&&st.stepDotActive, activeStep>step.id&&st.stepDotDone]}>
              {activeStep>step.id
                ? <Ionicons name="checkmark" size={12} color="#FFF" />
                : <Ionicons name={step.icon} size={14} color={activeStep===step.id?'#FFF':'#94a3b8'} />
              }
            </View>
            {idx < STEPS.length-1 && <View style={[st.stepLine, activeStep>step.id&&st.stepLineDone]} />}
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Score strip */}
      <View style={st.scoreStrip}>
        <View style={{flex:1}}>
          <View style={st.scoreBar}><View style={[st.scoreBarFill,{width:`${scoreValue}%`}]} /></View>
        </View>
        <Text style={st.scoreStripText}>{scoreValue}% complete</Text>
      </View>

      {/* Content */}
      <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':'height'} keyboardVerticalOffset={Platform.OS==='ios'?0:20}>
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{paddingBottom:24}}>
          {renderStep()}
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Nav buttons */}
      <View style={st.navRow}>
        <TouchableOpacity style={st.prevBtn} onPress={goPrev} disabled={activeStep===1}>
          <Ionicons name="chevron-back" size={20} color={activeStep===1?'#CBD5E1':'#0e4e96'} />
          <Text style={[st.prevBtnText,activeStep===1&&{color:'#CBD5E1'}]}>Back</Text>
        </TouchableOpacity>
        <View style={{alignItems:'center'}}>
          <Text style={{fontSize:11,color:'#94a3b8'}}>{activeStep} / {STEPS.length}</Text>
        </View>
        {activeStep < STEPS.length ? (
          <TouchableOpacity style={[st.nextBtn, !canProceed()&&st.nextBtnDisabled]} onPress={goNext} disabled={!canProceed()}>
            <Text style={st.nextBtnText}>Next</Text>
            <Ionicons name="chevron-forward" size={20} color={!canProceed()?'#CBD5E1':'#FFF'} />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={st.nextBtn}
            onPress={async () => {
              // Save & upload to VPS first, then forward to evaluate/preview screen
              try { await saveResumeToDocuments(); } catch (e) { console.warn('Finish save error:', e?.message); }
              navigation.navigate('EditEvaluateResume', {
                resumeData: {
                  personalInfo: { fullName, profession, email, phone, location, linkedin,
                                  profileImageUri: photoUri, profileImageBase64: photoB64 },
                  summary,
                  experience: experiences,
                  education,
                  skills,
                  certifications,
                  projects,
                  languages,
                  awards,
                  template: selectedTemplate,
                  resumeScore: calcScore(),
                  atsTest: atsResult || {},
                },
              });
            }}
          >
            <Text style={st.nextBtnText}>Finish</Text>
            <Ionicons name="checkmark" size={20} color="#FFF" />
          </TouchableOpacity>
        )}
      </View>

      {/* Modals */}
      {renderPreviewModal()}
      {renderCoverModal()}
      {renderVersionModal()}
    </SafeAreaView>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const AiTipsPanel = ({ show, onToggle, tips }) => (
  <>
    <TouchableOpacity style={st.tipBtn} onPress={onToggle}>
      <Ionicons name="bulb-outline" size={18} color="#F59E0B" />
      <Text style={st.tipBtnText}>{show ? 'Hide AI Tips' : 'Show AI Tips'}</Text>
    </TouchableOpacity>
    {show && (
      <View style={st.tipsBox}>
        {tips.map((t,i) => (
          <View key={i} style={{flexDirection:'row',alignItems:'flex-start',marginBottom:8}}>
            <Ionicons name="checkmark-circle" size={15} color="#19abb5" style={{marginTop:1,marginRight:8}} />
            <Text style={st.tipText}>{t}</Text>
          </View>
        ))}
      </View>
    )}
  </>
);

const SectionBlock = ({ title, icon, items, onAdd, renderItem }) => (
  <View style={{marginBottom:16}}>
    <View style={st.sectionHdr}>
      <Ionicons name={icon} size={18} color="#0e4e96" />
      <Text style={st.sectionHdrText}>{title}</Text>
      <View style={{flex:1}} />
      <TouchableOpacity style={st.sectionAddBtn} onPress={onAdd}>
        <Ionicons name="add" size={16} color="#0e4e96" />
        <Text style={st.sectionAddText}>Add</Text>
      </TouchableOpacity>
    </View>
    {items.map(item => renderItem(item))}
    {items.length===0 && <Text style={{fontSize:12,color:'#94a3b8',paddingLeft:4,paddingBottom:4}}>None added</Text>}
  </View>
);

const LinearGradientFallback = ({ accent, bg, children }) => (
  <View style={{ backgroundColor:bg, borderBottomWidth:3, borderBottomColor:accent }}>
    {children}
  </View>
);

const PreviewSection = ({ title, accent, children }) => (
  <View style={{marginBottom:16}}>
    <Text style={{fontSize:13,fontWeight:'900',color:accent,borderBottomWidth:2,borderBottomColor:accent,paddingBottom:4,marginBottom:10,textTransform:'uppercase',letterSpacing:0.8}}>{title}</Text>
    {children}
  </View>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const st = StyleSheet.create({
  root:         { flex:1, backgroundColor:'#FFFFFF' },
  header:       { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  headerTitle:  { fontSize:18, fontWeight:'700', color:'#0f172a' },
  headerSub:    { fontSize:11, color:'#94a3b8', marginTop:2 },
  backBtn:      { width:40, height:40, borderRadius:20, backgroundColor:'#F8FAFC', justifyContent:'center', alignItems:'center' },
  // Step progress
  stepsBar:     { backgroundColor:'#F8FAFC', paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#E2E8F0', flexGrow:0 },
  stepDotWrap:  { flexDirection:'row', alignItems:'center' },
  stepDot:      { width:30, height:30, borderRadius:15, backgroundColor:'#E2E8F0', justifyContent:'center', alignItems:'center' },
  stepDotActive:{ backgroundColor:'#0e4e96' },
  stepDotDone:  { backgroundColor:'#19abb5' },
  stepLine:     { width:18, height:2, backgroundColor:'#E2E8F0', marginHorizontal:2 },
  stepLineDone: { backgroundColor:'#19abb5' },
  // Score strip
  scoreStrip:   { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:8, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0', gap:10 },
  scoreBar:     { height:6, backgroundColor:'#E2E8F0', borderRadius:3, overflow:'hidden' },
  scoreBarFill: { height:6, backgroundColor:'#19abb5', borderRadius:3 },
  scoreStripText:{ fontSize:12, color:'#64748b', fontWeight:'600', width:80, textAlign:'right' },
  // Step content
  step:         { padding:16 },
  stepTitle:    { fontSize:22, fontWeight:'800', color:'#0f172a', marginBottom:6 },
  stepDesc:     { fontSize:14, color:'#64748b', marginBottom:20, lineHeight:20 },
  // Form
  fg:           { marginBottom:16 },
  fl:           { fontSize:14, fontWeight:'600', color:'#374151', marginBottom:6 },
  fw:           { flexDirection:'row', alignItems:'center', borderWidth:1.5, borderColor:'#D1D5DB', borderRadius:12, paddingHorizontal:14, backgroundColor:'#FAFAFA' },
  fi:           { flex:1, paddingVertical:14, fontSize:15, color:'#0f172a' },
  labelRow:     { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:6 },
  counterRow:   { flexDirection:'row', gap:12, marginTop:4 },
  counterText:  { fontSize:11, color:'#94a3b8' },
  dateRow:      { flexDirection:'row', marginBottom:0 },
  currentRow:   { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:16, paddingVertical:8, borderTopWidth:1, borderTopColor:'#F1F5F9', borderBottomWidth:1, borderBottomColor:'#F1F5F9' },
  // Photo
  photoCard:    { backgroundColor:'#F8FAFC', borderRadius:14, borderWidth:1, borderColor:'#E2E8F0', padding:14, marginBottom:20 },
  photo:        { width:60, height:60, borderRadius:30, borderWidth:2, borderColor:'#0e4e96' },
  photoPlaceholder:{ width:60,height:60,borderRadius:30,backgroundColor:'#E2E8F0',justifyContent:'center',alignItems:'center' },
  photoTitle:   { fontSize:14, fontWeight:'700', color:'#0f172a' },
  photoSub:     { fontSize:12, color:'#94a3b8', marginTop:2 },
  photoBtn:     { flexDirection:'row', alignItems:'center', paddingHorizontal:12, paddingVertical:7, borderRadius:8, backgroundColor:'#FFF', borderWidth:1, borderColor:'#E2E8F0', gap:5 },
  photoBtnText: { fontSize:12, fontWeight:'600', color:'#0e4e96' },
  photoBtnDanger:{ borderColor:'#FCA5A5' },
  // AI buttons
  aiBtn:        { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', paddingHorizontal:12, paddingVertical:7, borderRadius:8, gap:5 },
  aiBtnText:    { fontSize:12, fontWeight:'600', color:'#FFF' },
  aiSmBtn:      { flexDirection:'row', alignItems:'center', backgroundColor:'#8B5CF6', paddingHorizontal:9, paddingVertical:5, borderRadius:7, gap:4 },
  aiSmBtnText:  { fontSize:11, fontWeight:'600', color:'#FFF' },
  analyseBtn:   { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', paddingHorizontal:18, paddingVertical:12, borderRadius:12, gap:8, justifyContent:'center', marginBottom:4 },
  analyseBtnText:{ fontSize:14, fontWeight:'700', color:'#FFF' },
  // Suggestions
  sugBox:       { backgroundColor:'#F8FAFC', borderRadius:12, padding:14, borderWidth:1, borderColor:'#E2E8F0', marginTop:12 },
  sugTitle:     { fontSize:14, fontWeight:'700', color:'#0f172a', marginBottom:10 },
  sugItem:      { flexDirection:'row', alignItems:'flex-start', padding:12, backgroundColor:'#FFF', borderRadius:8, borderWidth:1, borderColor:'#E2E8F0', marginBottom:8, gap:8 },
  sugText:      { fontSize:13, color:'#374151', lineHeight:20, flex:1 },
  // Skills
  addSkillBtn:  { backgroundColor:'#0e4e96', padding:14, borderRadius:12, justifyContent:'center', alignItems:'center', width:48 },
  levelChip:    { paddingHorizontal:10, paddingVertical:5, borderRadius:16, backgroundColor:'#F1F5F9', borderWidth:1, borderColor:'#E2E8F0' },
  levelChipText:{ fontSize:12, color:'#374151', fontWeight:'500' },
  suggestRow:   { backgroundColor:'#EFF6FF', borderRadius:12, padding:12, marginBottom:14 },
  suggestLabel: { fontSize:12, color:'#1e40af', fontWeight:'600', marginBottom:8 },
  suggestChip:  { backgroundColor:'#DBEAFE', paddingHorizontal:12, paddingVertical:5, borderRadius:16, marginRight:8 },
  suggestChipText:{ fontSize:12, color:'#1d4ed8', fontWeight:'600' },
  skillRow:     { flexDirection:'row', alignItems:'center', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:8 },
  skillLvlDot:  { width:10, height:10, borderRadius:5 },
  skillName:    { fontSize:14, fontWeight:'600', color:'#0f172a', width:110 },
  skillLvlBtn:  { paddingHorizontal:8, paddingVertical:3, borderRadius:12, borderWidth:1, borderColor:'#D1D5DB', backgroundColor:'#F9FAFB' },
  skillLvlBtnText:{ fontSize:10, color:'#374151', fontWeight:'500' },
  // Cards
  card:         { backgroundColor:'#F8FAFC', borderRadius:14, padding:16, borderWidth:1, borderColor:'#E2E8F0', marginBottom:16 },
  cardHeader:   { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:14 },
  cardNum:      { fontSize:16, fontWeight:'700', color:'#0f172a' },
  delBtn:       { padding:6 },
  addBtn:       { flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:14, borderWidth:2, borderColor:'#0e4e96', borderStyle:'dashed', borderRadius:12, gap:8, marginBottom:16 },
  addBtnText:   { fontSize:14, fontWeight:'600', color:'#0e4e96' },
  // Extra sections
  extraCard:    { backgroundColor:'#FFF', borderRadius:10, padding:14, borderWidth:1, borderColor:'#E2E8F0', marginBottom:10 },
  sectionHdr:   { flexDirection:'row', alignItems:'center', paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#E2E8F0', marginBottom:10, gap:8 },
  sectionHdrText:{ fontSize:15, fontWeight:'700', color:'#0f172a' },
  sectionAddBtn:{ flexDirection:'row', alignItems:'center', backgroundColor:'#EFF6FF', borderRadius:8, paddingHorizontal:10, paddingVertical:5, gap:4 },
  sectionAddText:{ fontSize:12, color:'#0e4e96', fontWeight:'600' },
  // Templates
  tplCard:      { width:(width-48)/2, padding:14, backgroundColor:'#F8FAFC', borderRadius:14, borderWidth:2, borderColor:'#E2E8F0', alignItems:'center', position:'relative' },
  tplCardSelected:{ borderColor:'#19abb5', backgroundColor:'#F0FDFA' },
  tplPreview:   { width:'100%', height:90, borderRadius:8, padding:10, marginBottom:10 },
  tplName:      { fontSize:14, fontWeight:'700', color:'#0f172a' },
  tplDesc:      { fontSize:11, color:'#94a3b8', textAlign:'center', marginTop:2 },
  tplCheck:     { position:'absolute', top:8, right:8 },
  previewBtn:   { flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:13, borderWidth:1.5, borderColor:'#0e4e96', borderRadius:12, gap:8, marginTop:14, marginBottom:8 },
  previewBtnText:{ fontSize:14, fontWeight:'600', color:'#0e4e96' },
  // ATS
  scoreRingRow: { flexDirection:'row', alignItems:'center', marginBottom:14 },
  scoreRing:    { width:80, height:80, borderRadius:40, borderWidth:4, justifyContent:'center', alignItems:'center' },
  scoreRingVal: { fontSize:22, fontWeight:'900' },
  scoreRingLabel:{ fontSize:10, color:'#94a3b8' },
  atsScoreRow:  { flexDirection:'row', alignItems:'center', marginBottom:14 },
  atsScoreBig:  { width:80, height:80, borderRadius:40, borderWidth:4, justifyContent:'center', alignItems:'center' },
  atsScoreVal:  { fontSize:22, fontWeight:'900' },
  atsScoreLabel:{ fontSize:10, color:'#94a3b8' },
  atsBar:       { height:7, backgroundColor:'#E2E8F0', borderRadius:4, overflow:'hidden', marginTop:3 },
  atsBarFill:   { height:7, borderRadius:4 },
  missingKw:    { backgroundColor:'#FEF3C7', borderRadius:14, paddingHorizontal:10, paddingVertical:4, borderWidth:1, borderColor:'#FDE68A' },
  missingKwText:{ fontSize:11, color:'#92400E', fontWeight:'600' },
  recRow:       { flexDirection:'row', alignItems:'flex-start', gap:8, paddingVertical:6, borderTopWidth:1, borderTopColor:'#F1F5F9' },
  recText:      { flex:1, fontSize:12, color:'#374151', lineHeight:18 },
  // Export
  checkItem:    { flexDirection:'row', alignItems:'center', gap:8, marginBottom:6 },
  checkLabel:   { fontSize:12, color:'#374151' },
  fbRow:        { flexDirection:'row', alignItems:'flex-start', gap:10, padding:10, borderRadius:8, marginBottom:8, backgroundColor:'#FFFBEB' },
  fbText:       { flex:1, fontSize:13, color:'#0f172a', lineHeight:18 },
  exportRow:    { flexDirection:'row', alignItems:'center', paddingVertical:14, borderBottomWidth:1, borderBottomColor:'#F1F5F9', borderLeftWidth:3, paddingLeft:10, gap:12 },
  exportIcon:   { width:38, height:38, borderRadius:19, justifyContent:'center', alignItems:'center' },
  exportLabel:  { flex:1, fontSize:14, fontWeight:'600', color:'#0f172a' },
  installHint:  { fontSize:11, color:'#94a3b8', textAlign:'center', marginTop:8, fontStyle:'italic' },
  // Tips
  tipBtn:       { flexDirection:'row', alignItems:'center', alignSelf:'flex-start', backgroundColor:'#FFFBEB', paddingHorizontal:12, paddingVertical:7, borderRadius:8, marginTop:16, gap:6 },
  tipBtnText:   { fontSize:13, fontWeight:'600', color:'#92400E' },
  tipsBox:      { marginTop:10, padding:14, backgroundColor:'#FFFBEB', borderRadius:12, borderWidth:1, borderColor:'#FDE68A' },
  tipText:      { flex:1, fontSize:13, color:'#78350F', lineHeight:20 },
  // Empty
  emptyBox:     { alignItems:'center', paddingVertical:32 },
  emptyText:    { fontSize:15, fontWeight:'600', color:'#94a3b8', marginTop:10 },
  emptySub:     { fontSize:12, color:'#CBD5E1', marginTop:4, textAlign:'center' },
  // Versions
  verRow:       { flexDirection:'row', alignItems:'center', backgroundColor:'#F8FAFC', borderRadius:12, padding:14, marginBottom:10 },
  // Nav
  navRow:       { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, paddingVertical:14, borderTopWidth:1, borderTopColor:'#E2E8F0', backgroundColor:'#FFF' },
  prevBtn:      { flexDirection:'row', alignItems:'center', gap:4 },
  prevBtnText:  { fontSize:15, fontWeight:'600', color:'#0e4e96' },
  nextBtn:      { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', paddingVertical:12, paddingHorizontal:22, borderRadius:12, gap:4 },
  nextBtnText:  { fontSize:15, fontWeight:'700', color:'#FFF' },
  nextBtnDisabled:{ backgroundColor:'#CBD5E1' },
  // Modal
  modalOverlay: { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  modalSheet:   { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, overflow:'hidden' },
  modalHdr:     { flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:20, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  modalTitle:   { fontSize:18, fontWeight:'700', color:'#0f172a' },
  modalFooter:  { flexDirection:'row', gap:12, padding:16, borderTopWidth:1, borderTopColor:'#E2E8F0' },
  modalClosBtn: { flex:1, backgroundColor:'#F1F5F9', paddingVertical:13, borderRadius:12, alignItems:'center', justifyContent:'center', flexDirection:'row' },
  modalClosBtnText:{ fontSize:14, fontWeight:'700', color:'#0f172a' },
  previewScroll:{ backgroundColor:'#FFF' },
});

// ─── Field component (module scope — outside the screen component, so every
// keystroke does NOT remount the input). Wrapped in React.memo so unchanged
// fields skip re-render entirely.
const Field = React.memo(({ label, icon, value, onChange, placeholder, keyboard='default', multiline=false, required=false }) => (
  <View style={st.fg}>
    <Text style={st.fl}>{label}{required&&<Text style={{color:'#EF4444'}}> *</Text>}</Text>
    <View style={[st.fw, multiline && { alignItems:'flex-start', paddingVertical:12 }]}>
      {icon && <Ionicons name={icon} size={18} color="#94a3b8" style={{ marginRight:10, marginTop: multiline?2:0 }} />}
      <TextInput
        style={[st.fi, multiline && { minHeight:90, textAlignVertical:'top' }]}
        placeholder={placeholder}
        placeholderTextColor="#CBD5E1"
        value={value}
        onChangeText={onChange}
        keyboardType={keyboard}
        autoCapitalize={keyboard==='email-address'?'none':'sentences'}
        multiline={multiline}
      />
    </View>
  </View>
));

